// src/components/ReadableConditions/ReadableConditions.jsx
import React41 from "react";
import PropTypes19 from "prop-types";

// src/components/shape.js
import PropTypes from "prop-types";
import pick from "lodash.pick";

// src/components/Group.tsx
import { useContext as useContext17, useMemo as useMemo7, useState as useState11 } from "react";
import {
  Button as Button12,
  ButtonGroup,
  Dropdown,
  Icon as Icon3,
  IconButton as IconButton2,
  MenuList,
  Select as Select24,
  SplitButton
} from "@studio/legacy-components";

// src/lib/key-moments.ts
var KEY_MOMENT_EVENT_PREFIX = "appcues_synthetic:";
function isKeyMomentEvent(name) {
  return Boolean(name == null ? void 0 : name.startsWith(KEY_MOMENT_EVENT_PREFIX));
}
function keyMomentEventName(slug) {
  return `${KEY_MOMENT_EVENT_PREFIX}${slug}`;
}
function isKeyMoment(goal) {
  return (goal == null ? void 0 : goal.goalFormatVersion) === 3 && Boolean(goal.slug);
}
function labelFor(goal) {
  return goal.name || goal.slug;
}
function buildKeyMomentOptions(goals) {
  return (goals != null ? goals : []).filter(isKeyMoment).map((goal) => ({
    label: labelFor(goal),
    value: keyMomentEventName(goal.slug),
    isDisabled: Boolean(goal.disabled)
  })).sort(
    (a, b) => a.label.localeCompare(b.label, void 0, {
      numeric: true,
      sensitivity: "base"
    })
  );
}
function buildKeyMomentNameMap(goals) {
  return (goals != null ? goals : []).filter(isKeyMoment).reduce((acc, goal) => {
    acc[keyMomentEventName(goal.slug)] = labelFor(goal);
    return acc;
  }, {});
}
function excludeKeyMomentEvents(options) {
  return options.filter(({ value }) => !isKeyMomentEvent(value));
}

// src/components/AttributesClause.tsx
import { useRef, useState as useState2 } from "react";
import { Select as Select2, Input as Input2 } from "@studio/legacy-components";

// src/components/constants/operators.js
var OPERATORS = {
  EQUALS: { label: "equals", value: "==" },
  DOESNT_EQUAL: { label: "doesn't equal", value: "!=" },
  CONTAINS: { label: "contains", value: "*" },
  DOESNT_CONTAIN: { label: "doesn't contain", value: "!*" },
  STARTS_WITH: { label: "starts with", value: "^" },
  DOESNT_START_WITH: { label: "doesn't start with", value: "!^" },
  ENDS_WITH: { label: "ends with", value: "$" },
  DOESNT_END_WITH: { label: "doesn't end with", value: "!$" },
  EXISTS: { label: "exists", value: "?" },
  DOESNT_EXIST: { label: "doesn't exist", value: "!?" },
  IS_ONE_OF: { label: "is one of", value: "in" },
  ISNT_ONE_OF: { label: "isn't one of", value: "not in" },
  IS_GREATER_THAN: { label: "is greater than", value: ">" },
  IS_GREATER_OR_EQUAL: { label: "greater than or equal to", value: ">=" },
  IS_LESS_THAN: { label: "is less than", value: "<" },
  IS_LESS_OR_EQUAL: { label: "less than or equal to", value: "<=" },
  OCCURRED_MORE_THAN: { label: "occurred more than", value: "> ago" },
  OCCURRED_LESS_THAN: { label: "occurred less than", value: "< ago" },
  WITHIN: {
    label: "will occur within the next",
    value: "within"
  },
  OCCURRED_BEFORE: { label: "occurred before", value: "< time" },
  OCCURRED_AFTER: { label: "occurred after", value: "> time" },
  MATCHES_REGEX: { label: "matches regex", value: "regex" }
};
var PROPERTY_CLAUSE_OPERATORS = [
  OPERATORS.EQUALS,
  OPERATORS.DOESNT_EQUAL,
  OPERATORS.CONTAINS,
  OPERATORS.DOESNT_CONTAIN
];
var ALL_OPERATORS = Object.values(OPERATORS);
var DEFAULT_OPERATORS = /* @__PURE__ */ new Set([
  "==",
  "!=",
  "*",
  "!*",
  "^",
  "!^",
  "$",
  "!$",
  "regex"
]);
var MULTI_VALUE_OPERATORS = /* @__PURE__ */ new Set(["in", "not in"]);
var NUMERIC_OPERATORS = /* @__PURE__ */ new Set([">", "<", "<=", ">="]);
var RELATIVE_TIME_OPERATORS = /* @__PURE__ */ new Set(["> ago", "< ago", "within"]);
var ABSOLUTE_TIME_OPERATORS = /* @__PURE__ */ new Set(["> time", "< time"]);
var EXISTENTIAL_OPERATORS = /* @__PURE__ */ new Set(["?", "!?"]);

// src/components/PropertyTextArea.tsx
import { useMemo, useState } from "react";
import styled from "styled-components";
import { Text, Alert } from "@appcues/sonar";
import { TextArea } from "@studio/legacy-components";
import { jsx, jsxs } from "react/jsx-runtime";
var Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${TextArea} {
    width: 100%;
  }
`;
var PropertyTextArea = ({ defaultValue, onChange, onBlur }) => {
  const [isErrored, setIsErrored] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const numValues = useMemo(() => {
    var _a;
    return (_a = String(value)) == null ? void 0 : _a.split("\n").length;
  }, [value]);
  const handleChange = (event) => {
    const {
      target: { value: nextValue }
    } = event;
    const values = nextValue.replace(/(\s*,\s*)|\n/g, ",\n").replace(/\s*,\s*/g, ",").replace(/(^[\t ]*\n*\s+$)/gm, "").split(",");
    const trimmed = values.slice(0, 1e3).join("\n");
    setValue(trimmed);
    setIsErrored(values.length > 1e3);
    event.target.value = trimmed;
    onChange(event);
  };
  const handleErrorDismissed = () => setIsErrored(false);
  return /* @__PURE__ */ jsxs(Wrapper, {
    children: [
      isErrored && /* @__PURE__ */ jsx(Alert, {
        variant: "danger",
        onDismiss: handleErrorDismissed,
        children: "Heads up! You've exceeded the 1,000 value limit. Only the first 1,000 entries will be saved."
      }),
      /* @__PURE__ */ jsx(TextArea, {
        resize: true,
        name: "value",
        "aria-label": "value",
        value,
        placeholder: "Enter values separated by commas or line breaks",
        onChange: handleChange,
        onBlur
      }),
      /* @__PURE__ */ jsx(Text, {
        children: `${numValues}/1000`
      })
    ]
  });
};
var PropertyTextArea_default = PropertyTextArea;

// src/components/styled.tsx
import styled2 from "styled-components";
import {
  DataList,
  DateInput,
  IconButton,
  Input,
  Select,
  Button,
  StatusLabel,
  TextArea as TextArea2,
  WarningNotice
} from "@studio/legacy-components";
var UpgradeBadge = styled2(StatusLabel)`
  font-weight: var(--normal);
  word-break: keep-all;
  user-select: none;
`;
var ConditionsWrapper = styled2.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding-bottom: 16px;

  > ${Select} {
    max-width: unset;
    min-width: unset;
  }
`;
var Bracket = styled2.div`
  align-self: stretch;
  border: 2px solid
    ${({ type }) => type === "and" ? "var(--blurple)" : "var(--sea-monsters)"};
  border-right-width: 0;
  display: flex;
  margin: 9px 0 20px 0;
  min-width: 45px;
`;
var BracketWrapper = styled2.div`
  align-items: center;
  display: flex;
  min-width: 70px;

  > ${Select} {
    min-width: 70px;
  }

  > ${Bracket} {
    margin-left: -35px;
  }
`;
var Content = styled2.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
var Child = styled2.div`
  --options-margin: calc(11em + 12px); // width of first Select + gap

  display: flex;
  justify-content: flex-start;
  gap: 12px;
  position: relative;

  ${Select} {
    min-width: 11em;
    height: 40px;
  }

  ${IconButton} {
    height: 40px;
  }
`;
var Children = styled2.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  > ${ConditionsWrapper} {
    padding-bottom: 0;
  }
`;
var ConditionsType = styled2.span`
  align-items: center;
  color: var(--pleather);
  display: flex;
  font-weight: var(--bold);
`;
var ClauseWrapper = styled2.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;

  ${TextArea2} {
    margin-left: calc(-1 * var(--options-margin));
    resize: vertical;
    width: calc(
      100% + var(--options-margin) + 32px
    ); // final value is button width + gap
  }
`;
var Clause = styled2.div.attrs({
  className: "conditions-clause__wrapper"
})`
  display: flex;
  align-items: center;
  gap: 12px;

  ${Select} {
    &:first-child {
      min-width: 15em;
    }

    ${UpgradeBadge} {
      margin-top: 6px;
    }
  }

  ${Select}, ${Input}, ${DateInput}, ${DataList} {
    &:nth-last-child(2),
    &:last-of-type {
      min-width: 15em;
    }
  }
`;
var TimeframeWrapper = styled2.div`
  align-items: center;
  display: flex;
  gap: 6px;
  margin-left: calc(-1 * var(--options-margin));

  ${Input}[type=number] {
    width: 6em;
  }

  > ${Select} {
    min-width: 12em;
  }
`;
var VersionClauseWrapper = styled2.div`
  ${WarningNotice} {
    margin-top: 16px;
    max-width: calc(30em + 12px);

    > div {
      line-height: var(--large);
    }
  }
`;
var Wrapper2 = styled2.div``;
var ToggleButton = styled2(Button)`
  min-width: 186px;
`;
var TextAreaWrapper = styled2.div`
  width: 100%;
  margin-top: 4px;
`;

// src/components/AttributesClause.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ATTRIBUTES_OPERATORS = [
  OPERATORS.EQUALS,
  OPERATORS.DOESNT_EQUAL,
  OPERATORS.CONTAINS,
  OPERATORS.DOESNT_CONTAIN,
  OPERATORS.STARTS_WITH,
  OPERATORS.DOESNT_START_WITH,
  OPERATORS.ENDS_WITH,
  OPERATORS.DOESNT_END_WITH,
  OPERATORS.EXISTS,
  OPERATORS.DOESNT_EXIST,
  OPERATORS.IS_ONE_OF,
  OPERATORS.ISNT_ONE_OF,
  OPERATORS.IS_GREATER_THAN,
  OPERATORS.IS_LESS_THAN,
  OPERATORS.MATCHES_REGEX
];
var ATTRIBUTES_KEY = "attributes";
var checkMultiValueOperator = (value) => [OPERATORS.IS_ONE_OF.value, OPERATORS.ISNT_ONE_OF.value].includes(value);
function AttributesClause({ conditions, onChange }) {
  const attributeRef = useRef();
  const valueRef = useRef();
  const [showOptions, toggleExpanded] = useState2(false);
  const [multiValue, setMultiValue] = useState2("");
  const {
    [ATTRIBUTES_KEY]: {
      attribute: currentAttribute = "",
      operator: currentOperator,
      value: currentValue = ""
    }
  } = conditions;
  const handleChange = (changes) => void onChange({
    [ATTRIBUTES_KEY]: { ...conditions[ATTRIBUTES_KEY], ...changes }
  });
  const handleToggle = () => {
    toggleExpanded((val) => !val);
  };
  const handleOperatorChange = ({
    value: operator
  }) => {
    void handleChange({ operator });
    if (checkMultiValueOperator(operator)) {
      toggleExpanded(true);
    }
  };
  const handleMultiValueInputBlur = ({
    target: { value }
  }) => {
    void handleChange({ value });
  };
  const valueCounter = () => {
    const values = multiValue != null ? multiValue : currentValue;
    return (values == null ? void 0 : values.length) > 0 ? values == null ? void 0 : values.split("\n").length : -1;
  };
  const handleMultiValueInputChange = ({
    target: { value }
  }) => {
    setMultiValue(value);
  };
  const handleInputBlur = (name) => ({ target: { value } }) => void handleChange({ [name]: value });
  const isMultiValueOperator = checkMultiValueOperator(currentOperator);
  const multiValueCounter = valueCounter();
  return /* @__PURE__ */ jsxs2(Wrapper2, {
    children: [
      /* @__PURE__ */ jsxs2(Clause, {
        children: [
          /* @__PURE__ */ jsx2(Input2, {
            name: "attribute",
            "aria-label": "attribute",
            type: "text",
            ref: attributeRef,
            defaultValue: currentAttribute,
            placeholder: "Enter attribute",
            onBlur: handleInputBlur("attribute")
          }),
          /* @__PURE__ */ jsx2(Select2, {
            name: "operator",
            value: ATTRIBUTES_OPERATORS.find(
              ({ value }) => value === currentOperator
            ),
            options: ATTRIBUTES_OPERATORS,
            onChange: handleOperatorChange,
            menuShouldBlockScroll: true,
            portal: true
          }),
          !isMultiValueOperator && /* @__PURE__ */ jsx2(Input2, {
            name: "value",
            "aria-label": "value",
            type: "text",
            ref: valueRef,
            defaultValue: currentValue,
            placeholder: "Enter attribute value",
            onBlur: handleInputBlur("value")
          }),
          isMultiValueOperator && /* @__PURE__ */ jsxs2(ToggleButton, {
            kind: "secondary",
            onClick: handleToggle,
            children: [
              showOptions ? "Hide" : "Show",
              " ",
              multiValueCounter,
              " value",
              multiValueCounter > 1 ? "s" : ""
            ]
          })
        ]
      }),
      " ",
      /* @__PURE__ */ jsx2(TextAreaWrapper, {
        children: showOptions && isMultiValueOperator && /* @__PURE__ */ jsx2(PropertyTextArea_default, {
          defaultValue: currentValue,
          onBlur: handleMultiValueInputBlur,
          onChange: handleMultiValueInputChange
        })
      })
    ]
  });
}

// src/components/BannersClause.tsx
import { useCallback, useContext, useMemo as useMemo2 } from "react";
import { Select as Select4 } from "@studio/legacy-components";

// src/components/ConditionsContext.ts
import { createContext } from "react";
var ConditionsContext = createContext({});

// src/components/QualificationWrapper.tsx
import { Icon as Icon2, Tooltip } from "@studio/legacy-components";

// ../../node_modules/@fortawesome/pro-regular-svg-icons/index.mjs
var faShieldCheck = {
  prefix: "far",
  iconName: "shield-check",
  icon: [512, 512, [], "f2f7", "M57 127L240 49.4 423 127c5.9 2.5 9.1 7.8 9 12.8c-.4 91.4-38.4 249.3-186.3 320.1c-3.6 1.7-7.8 1.7-11.3 0C86.4 389 48.5 231.2 48 139.7c0-5 3.1-10.2 9-12.8zM441.7 82.8L253.4 2.9C249.2 1 244.7 0 240 0s-9.2 1-13.4 2.9L38.3 82.8C16.3 92.1-.1 113.8 0 140c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C438.8 420.7 479.5 239.2 480 140c.1-26.2-16.3-47.9-38.3-57.2zM353 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L353 209z"]
};
var faShieldXmark = {
  prefix: "far",
  iconName: "shield-xmark",
  icon: [512, 512, ["shield-times"], "e24c", "M57 127L240 49.4 423 127c5.9 2.5 9.1 7.8 9 12.8c-.4 91.4-38.4 249.3-186.3 320.1c-3.6 1.7-7.8 1.7-11.3 0C86.4 389 48.5 231.2 48 139.7c0-5 3.1-10.2 9-12.8zM441.7 82.8L253.4 2.9C249.2 1 244.7 0 240 0s-9.2 1-13.4 2.9L38.3 82.8C16.3 92.1-.1 113.8 0 140c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C438.8 420.7 479.5 239.2 480 140c.1-26.2-16.3-47.9-38.3-57.2zM159 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z"]
};

// src/components/QualificationWrapper.styled.ts
import styled3 from "styled-components";
import { Select as Select3, Icon } from "@studio/legacy-components";
var Container = styled3.div`
  position: relative;
  input,
  ${Select3} div,
  textarea {
    border-radius: var(--border-radius-small);
    background-color: var(
      --${(props) => props.$qualified ? "ice-ice-baby" : "bubble-tape"}
    );
  }
  ${Icon} {
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0.5em;
    color: var(
      --${(props) => props.$qualified ? "sea-monsters" : "motorola-razr"}
    );
    background-color: var(
      --${(props) => props.$qualified ? "ice-ice-baby" : "bubble-tape"}
    );
    padding: 0.25em;
  }
  ${Select3} + ${Icon} {
    right: 2em;
  }
`;

// src/components/QualificationWrapper.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var QualificationWrapper = ({
  conditions,
  negated,
  children
}) => {
  const hasQualified = Object.hasOwn(conditions, "_qualified");
  if (!hasQualified) {
    return /* @__PURE__ */ jsx3(Fragment, {
      children
    });
  }
  const { _qualified, _value, _times } = conditions;
  const qualified = negated ? !_qualified : _qualified;
  const icon = qualified ? faShieldCheck : faShieldXmark;
  let tooltipLabel = /* @__PURE__ */ jsxs3(Fragment, {
    children: [
      "User ",
      _qualified ? "matches" : "does not match",
      " this condition"
    ]
  });
  if (Object.hasOwn(conditions, "_value")) {
    tooltipLabel = /* @__PURE__ */ jsxs3(Fragment, {
      children: [
        "Actual value for this user is ",
        /* @__PURE__ */ jsx3("b", {
          children: JSON.stringify(_value)
        })
      ]
    });
  }
  if (Object.hasOwn(conditions, "_times")) {
    const times = /* @__PURE__ */ jsxs3("b", {
      children: [
        _times,
        " time",
        _times === 1 ? "" : "s"
      ]
    });
    tooltipLabel = /* @__PURE__ */ jsxs3(Fragment, {
      children: [
        "User has triggered this event ",
        times,
        " in the specified timeframe"
      ]
    });
  }
  return /* @__PURE__ */ jsx3(Tooltip, {
    placement: "top",
    disabled: !tooltipLabel,
    label: tooltipLabel,
    children: /* @__PURE__ */ jsxs3(Container, {
      $qualified: qualified != null ? qualified : false,
      children: [
        children,
        /* @__PURE__ */ jsx3(Icon2, {
          icon
        })
      ]
    })
  });
};

// src/components/BannersClause.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var SEEN_EVENT_OPTIONS = [
  {
    label: "has been seen",
    value: "has been seen",
    negation: false
  },
  {
    label: "has not been seen",
    value: "has not been seen",
    negation: true
  }
];
var DISMISSED_EVENT_OPTIONS = [
  {
    label: "has been dismissed",
    value: "has been dismissed",
    negation: false
  },
  {
    label: "has not been dismissed",
    value: "has not been dismissed",
    negation: true
  }
];
var getCondition = ({ value, negation }, experience) => {
  if (value.includes("dismissed")) {
    return {
      experienceDismissed: {
        experienceId: experience.id,
        operator: negation ? "!=" : "==",
        value: "permanently_dismissed",
        type: "banners"
      }
    };
  }
  return {
    experienceStep: {
      experienceStep: experience == null ? void 0 : experience.stepId,
      type: "banners"
    }
  };
};
var BannersClause = ({ conditions, negated, onChange }) => {
  const { experienceStep: { experienceStep } = {} } = conditions;
  const { experienceDismissed } = conditions;
  const { experienceId } = experienceDismissed || { experienceId: "" };
  const conditionOptions = [...SEEN_EVENT_OPTIONS, ...DISMISSED_EVENT_OPTIONS];
  const { bannerOptions = [] } = useContext(ConditionsContext);
  const bannersOptions = useMemo2(() => [...bannerOptions], [bannerOptions]);
  if (experienceStep && !bannersOptions.some(({ stepId }) => stepId === experienceStep)) {
    bannersOptions.push({
      label: experienceStep,
      value: experienceStep,
      stepId: experienceStep
    });
  }
  const selectedExperience = useMemo2(() => {
    const experienceData = {
      stepId: experienceStep,
      experienceId
    };
    return bannersOptions.find(
      ({ value: bannerId, stepId }) => bannerId === experienceData.experienceId || stepId === experienceData.stepId
    );
  }, [bannersOptions, experienceStep, experienceId]);
  const selectedOption = useMemo2(() => {
    if (experienceDismissed) {
      const isNegation = experienceDismissed.operator === "!=";
      return DISMISSED_EVENT_OPTIONS.find(
        (event) => event.negation === isNegation
      );
    }
    return SEEN_EVENT_OPTIONS.find(
      (event) => event.negation === negated
    );
  }, [negated, experienceDismissed]);
  const handleBannerChange = ({
    value: bannerId,
    stepId
  }) => {
    onChange(
      { ...getCondition(selectedOption, { id: bannerId, stepId }) },
      negated
    );
  };
  const handleConditionChange = useCallback(
    (newCondition) => {
      const { negation } = newCondition;
      const condition = getCondition(newCondition, {
        id: selectedExperience == null ? void 0 : selectedExperience.value,
        stepId: selectedExperience == null ? void 0 : selectedExperience.stepId
      });
      if (condition.experienceDismissed) {
        onChange({ ...condition }, false);
        return;
      }
      onChange({ ...condition }, negation);
    },
    [onChange, selectedExperience]
  );
  return /* @__PURE__ */ jsx4(ClauseWrapper, {
    children: /* @__PURE__ */ jsxs4(Clause, {
      children: [
        /* @__PURE__ */ jsx4(Select4, {
          value: selectedExperience,
          options: bannersOptions,
          onChange: handleBannerChange,
          portal: true,
          menuShouldBlockScroll: true
        }),
        /* @__PURE__ */ jsx4(QualificationWrapper, {
          conditions,
          negated,
          children: /* @__PURE__ */ jsx4(Select4, {
            value: selectedOption,
            options: conditionOptions,
            onChange: handleConditionChange,
            portal: true,
            menuShouldBlockScroll: true
          })
        })
      ]
    })
  });
};

// src/components/ChecklistsClause.tsx
import { useContext as useContext2 } from "react";
import { Select as Select5 } from "@studio/legacy-components";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var STATUS_OPTIONS = [
  { label: "is in progress", value: "in_progress" },
  { label: "has been skipped", value: "skipped" },
  { label: "has been completed", value: "completed\ndismissed" },
  {
    label: "has not been seen",
    value: null
  }
];
var ChecklistsClause = ({ conditions, negated, onChange }) => {
  if (negated) {
    throw new Error("Checklists conditions cannot be negated");
  }
  const {
    checklists: { checklist, status = null }
  } = conditions;
  const { checklistOptions } = useContext2(ConditionsContext);
  const handleChange = (changes) => {
    onChange({ checklists: { ...conditions.checklists, ...changes } });
  };
  const handleChecklistChange = ({
    value: newChecklist
  }) => handleChange({ checklist: newChecklist });
  const handleStatusChange = ({
    value: newStatus
  }) => {
    handleChange({ status: newStatus });
  };
  const options = [...checklistOptions];
  if (checklist && !checklistOptions.some(
    ({ value: checklistId }) => checklistId === checklist
  )) {
    options.push({ label: checklist, value: checklist });
  }
  return /* @__PURE__ */ jsxs5(Clause, {
    children: [
      /* @__PURE__ */ jsx5(Select5, {
        value: options.find(({ value }) => value === checklist),
        options,
        onChange: handleChecklistChange,
        portal: true,
        menuShouldBlockScroll: true
      }),
      /* @__PURE__ */ jsx5(QualificationWrapper, {
        conditions,
        negated,
        children: /* @__PURE__ */ jsx5(Select5, {
          value: STATUS_OPTIONS.find(({ value }) => value === status),
          options: STATUS_OPTIONS,
          onChange: handleStatusChange,
          portal: true,
          menuShouldBlockScroll: true
        })
      })
    ]
  });
};

// src/components/ContentClause.tsx
import { useContext as useContext3 } from "react";
import { Select as Select7, Button as Button2 } from "@studio/legacy-components";

// src/lib/use-storage-toggle.js
import { useState as useState3, useLayoutEffect } from "react";
var useStorageToggle = (pageKey) => {
  const [showOptions, setShowOptions] = useState3(false);
  useLayoutEffect(() => {
    const storedValue = sessionStorage.getItem(pageKey);
    if (storedValue !== null) {
      setShowOptions(storedValue === "true");
    }
  }, [pageKey]);
  const toggleShowOptions = () => {
    setShowOptions((prev) => {
      const newValue = !prev;
      sessionStorage.setItem(pageKey, newValue ? "true" : "false");
      return newValue;
    });
  };
  return [showOptions, toggleShowOptions];
};

// src/components/TimeframeOptions.tsx
import { useRef as useRef2, useEffect, useState as useState4 } from "react";
import { Select as Select6, Input as Input3, DateInput as DateInput2, startOf } from "@studio/legacy-components";
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var OPERATOR_OPTIONS = [
  { label: "previously", value: "" },
  { label: "in the last", value: "< ago" },
  { label: "since", value: "since" }
];
var MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1e3;
var TimeframeOptions = ({
  times = 1,
  operator = "",
  value,
  onChange,
  disableTimes = false
}) => {
  const timesRef = useRef2(null);
  const [relativeTimeValue, setRelativeTimeValue] = useState4(-1);
  useEffect(() => {
    if (timesRef.current) {
      timesRef.current.value = String(times);
    }
  }, [times]);
  useEffect(() => {
    if (operator === "< ago") {
      const numDays = value != null ? value : 1;
      setRelativeTimeValue(numDays / MILLISECONDS_PER_DAY);
    }
  }, [operator, value]);
  const handleOperatorChange = ({
    value: newOperator
  }) => {
    if (newOperator === "< ago") {
      onChange({ operator: newOperator, value: MILLISECONDS_PER_DAY });
    } else if (newOperator === "since") {
      onChange({ operator: newOperator, value: Date.now() });
    } else {
      onChange({ operator: newOperator, value: void 0 });
    }
  };
  const handleTimesBlur = ({
    target: { valueAsNumber: newValue }
  }) => {
    let nextValue = newValue;
    if (!nextValue) {
      nextValue = 1;
      if (timesRef.current) {
        timesRef.current.value = String(nextValue);
      }
    }
    onChange({
      times: nextValue
    });
  };
  const handleRelativeTimeChange = ({
    target: { valueAsNumber }
  }) => void setRelativeTimeValue(valueAsNumber);
  const handleRelativeTimeBlur = ({
    target: { valueAsNumber }
  }) => {
    let nextValue = valueAsNumber;
    if (!nextValue) {
      nextValue = 1;
      setRelativeTimeValue(nextValue);
    }
    onChange({
      value: nextValue * MILLISECONDS_PER_DAY
    });
  };
  const handleDateChange = (date) => void onChange({
    value: startOf(date, "day").valueOf()
  });
  return /* @__PURE__ */ jsxs6(TimeframeWrapper, {
    children: [
      /* @__PURE__ */ jsx6("span", {
        children: "At least"
      }),
      /* @__PURE__ */ jsx6(Input3, {
        type: "number",
        name: "times",
        "aria-label": "times",
        ref: timesRef,
        defaultValue: times,
        onBlur: handleTimesBlur,
        disabled: disableTimes
      }),
      " ",
      /* @__PURE__ */ jsx6("span", {
        children: times === 1 ? "time" : "times"
      }),
      " ",
      /* @__PURE__ */ jsx6(Select6, {
        name: "operator",
        value: OPERATOR_OPTIONS.find(
          ({ value: optionOperator }) => optionOperator === operator
        ),
        options: OPERATOR_OPTIONS,
        onChange: handleOperatorChange,
        portal: true
      }),
      operator === "< ago" && /* @__PURE__ */ jsxs6(Fragment2, {
        children: [
          /* @__PURE__ */ jsx6(Input3, {
            type: "number",
            name: "value",
            "aria-label": "value",
            value: relativeTimeValue,
            onChange: handleRelativeTimeChange,
            onBlur: handleRelativeTimeBlur
          }),
          /* @__PURE__ */ jsx6("span", {
            children: relativeTimeValue === 1 ? "day" : "days"
          })
        ]
      }),
      operator === "since" && /* @__PURE__ */ jsx6(DateInput2, {
        future: true,
        name: "since",
        label: "since",
        value: value ? new Date(value) : new Date(),
        onChange: handleDateChange,
        placement: "top",
        portal: true
      })
    ]
  });
};

// src/components/ContentClause.tsx
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var PAGE_KEY = "content-clause";
var FLOW_EXPERIENCE_TYPE = "flow";
var DEFAULT_FLOW_EXPERIENCE_EVENT = "appcues:v2:experience_started";
var FLOW_EXPERIENCE_EVENT_OPTIONS = [
  {
    label: "has been seen",
    value: "appcues:v2:experience_started",
    event: "appcues:v2:experience_started",
    negation: false
  },
  {
    label: "has not been seen",
    value: "appcues:v2:experience_started:negated",
    event: "appcues:v2:experience_started",
    negation: true
  },
  {
    label: "has been completed",
    value: "appcues:v2:experience_completed",
    event: "appcues:v2:experience_completed",
    negation: false
  },
  {
    label: "has not been completed",
    value: "appcues:v2:experience_completed:negated",
    event: "appcues:v2:experience_completed",
    negation: true
  },
  {
    label: "has been dismissed",
    value: "appcues:v2:experience_dismissed",
    event: "appcues:v2:experience_dismissed",
    negation: false
  },
  {
    label: "has not been dismissed",
    value: "appcues:v2:experience_dismissed:negated",
    event: "appcues:v2:experience_dismissed",
    negation: true
  }
];
var isExperienceCondition = (conditions) => Object.hasOwn(conditions, "experience");
var ContentClause = ({ conditions, negated, onChange }) => {
  var _a, _b;
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY);
  const isExperience = isExperienceCondition(conditions);
  const currentConditions = isExperience ? conditions.experience : conditions.content;
  const selectedFlowId = isExperience ? conditions.experience.experience : conditions.content.content;
  const { operator } = currentConditions;
  const savedTimes = currentConditions.times;
  const savedValue = currentConditions.value;
  const times = savedTimes != null ? savedTimes : 1;
  const value = savedValue != null ? savedValue : 1;
  const currentEvent = isExperience ? (_a = conditions.experience.event) != null ? _a : DEFAULT_FLOW_EXPERIENCE_EVENT : DEFAULT_FLOW_EXPERIENCE_EVENT;
  const { flowOptions } = useContext3(ConditionsContext);
  const getTimeframeConditions = () => ({
    ...operator ? { operator } : {},
    ...savedTimes !== void 0 ? { times: savedTimes } : {},
    ...savedValue !== void 0 ? { value: savedValue } : {}
  });
  const getExperienceConditions = () => isExperience ? conditions.experience : {
    ...getTimeframeConditions(),
    type: FLOW_EXPERIENCE_TYPE,
    experience: selectedFlowId,
    event: DEFAULT_FLOW_EXPERIENCE_EVENT
  };
  const handleContentChange = (changes, nextNegated = negated) => {
    const baseContent = isExperience ? { content: selectedFlowId, ...getTimeframeConditions() } : conditions.content;
    onChange({ content: { ...baseContent, ...changes } }, nextNegated);
  };
  const handleExperienceChange = (changes, nextNegated = negated) => {
    const baseExperience = getExperienceConditions();
    onChange(
      {
        experience: {
          ...baseExperience,
          ...changes,
          type: FLOW_EXPERIENCE_TYPE
        }
      },
      nextNegated
    );
  };
  const handleExperienceTimeframeChange = (changes) => {
    const baseExperience = getExperienceConditions();
    onChange(
      {
        experience: {
          ...baseExperience,
          ...changes,
          type: FLOW_EXPERIENCE_TYPE
        }
      },
      negated
    );
  };
  const handleFlowChange = (option) => {
    const { value: nextFlowId, flowTargetingType } = option;
    if (flowTargetingType === "experience") {
      const nextNegated = isExperience ? negated : false;
      handleExperienceChange(
        { experience: nextFlowId, event: currentEvent },
        nextNegated
      );
      return;
    }
    handleContentChange(
      { content: nextFlowId },
      isExperience ? false : negated
    );
  };
  const handleNegationChange = ({
    value: nextNegated
  }) => handleContentChange({}, nextNegated);
  const handleEventChange = ({
    event: event2,
    negation
  }) => {
    handleExperienceChange({ event: event2 }, negation);
  };
  const handleTimeframeChange = (changes) => {
    if (isExperience) {
      handleExperienceTimeframeChange(changes);
    } else {
      handleContentChange(changes);
    }
  };
  const options = [...flowOptions];
  if (selectedFlowId && !options.some(({ value: optionFlowId }) => optionFlowId === selectedFlowId)) {
    options.push({
      label: selectedFlowId,
      value: selectedFlowId,
      flowTargetingType: isExperience ? "experience" : "legacy"
    });
  }
  const selectedFlow = options.find(
    ({ value: optionFlowId }) => optionFlowId === selectedFlowId
  );
  const selectedFlowIsExperience = isExperience || (selectedFlow == null ? void 0 : selectedFlow.flowTargetingType) === "experience";
  let event = "seen";
  if (selectedFlow && selectedFlow.stepType && ["tooltip-group", "hotspot-group"].includes(selectedFlow.stepType)) {
    event = "completed";
  }
  const negatedOptions = [
    { label: `has been ${event}`, value: false },
    { label: `has not been ${event}`, value: true }
  ];
  const selectedExperienceEvent = (_b = FLOW_EXPERIENCE_EVENT_OPTIONS.find(
    ({ event: optionEvent, negation }) => optionEvent === currentEvent && negated === negation
  )) != null ? _b : FLOW_EXPERIENCE_EVENT_OPTIONS[0];
  return /* @__PURE__ */ jsxs7(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs7(Clause, {
        children: [
          /* @__PURE__ */ jsx7(Select7, {
            value: selectedFlow,
            options,
            onChange: handleFlowChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx7(QualificationWrapper, {
            conditions,
            negated,
            children: selectedFlowIsExperience ? /* @__PURE__ */ jsx7(Select7, {
              value: selectedExperienceEvent,
              options: FLOW_EXPERIENCE_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            }) : /* @__PURE__ */ jsx7(Select7, {
              value: negated ? negatedOptions[1] : negatedOptions[0],
              options: negatedOptions,
              onChange: handleNegationChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs7(Button2, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx7(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleTimeframeChange
      })
    ]
  });
};

// src/components/EmailClause.tsx
import { useContext as useContext4 } from "react";
import { Select as Select8, Button as Button3 } from "@studio/legacy-components";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var PAGE_KEY2 = "emails-clause";
var EMAIL_EVENT_OPTIONS = [
  {
    label: "has been delivered",
    value: "delivered",
    negation: false
  },
  {
    label: "has not been delivered",
    value: "delivered:negated",
    negation: true
  },
  {
    label: "has been opened",
    value: "opened",
    negation: false
  },
  {
    label: "has not been opened",
    value: "opened:negated",
    negation: true
  },
  {
    label: "has been clicked",
    value: "clicked",
    negation: false
  },
  {
    label: "has not been clicked",
    value: "clicked:negated",
    negation: true
  },
  {
    label: "marked as spam",
    value: "spam",
    negation: false
  },
  {
    label: "was not marked spam",
    value: "spam:negated",
    negation: true
  },
  {
    label: "failed",
    value: "failed",
    negation: false
  },
  {
    label: "has not failed",
    value: "failed:negated",
    negation: true
  },
  {
    label: "skipped",
    value: "skipped",
    negation: false
  },
  {
    label: "has not skipped",
    value: "skipped:negated",
    negation: true
  }
];
var EVENTS_WITH_TIMES = /* @__PURE__ */ new Set(["opened", "clicked"]);
var EmailClause = ({ conditions, negated, onChange }) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY2);
  const {
    email: { email, operator, event, times = 1, value = 1 }
  } = conditions;
  const { emailOptions } = useContext4(ConditionsContext);
  const getDisplayLabel = ({ label, subject }) => (!label || label === "Untitled") && subject ? subject.trim() : label.trim();
  const customEmailOptions = emailOptions.sort((a, b) => getDisplayLabel(a).localeCompare(getDisplayLabel(b))).map((option) => ({
    ...option,
    label: getDisplayLabel(option)
  }));
  const selectedEmail = customEmailOptions.find(
    ({ value: emailOption }) => emailOption === email
  );
  const selectedEvent = EMAIL_EVENT_OPTIONS.find(
    ({ value: eventValue, negation: eventNegation }) => {
      const [eventType] = eventValue.split(":");
      return eventType === event && negated === eventNegation;
    }
  );
  const handleChange = (changes, nextNegated = negated) => {
    var _a;
    const updatedChanges = { ...changes };
    if (changes.event) {
      const shouldHaveTimes = EVENTS_WITH_TIMES.has(changes.event);
      updatedChanges.times = shouldHaveTimes ? (_a = changes.times) != null ? _a : 1 : void 0;
    }
    onChange(
      { email: { ...conditions.email, ...updatedChanges } },
      nextNegated
    );
  };
  const handleEmailChange = ({ value: newContent }) => handleChange({ email: newContent });
  const handleEventChange = ({
    value: eventValue,
    negation: eventNegation
  }) => {
    const [eventType] = eventValue.split(":");
    handleChange({ event: eventType }, eventNegation);
  };
  return /* @__PURE__ */ jsxs8(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs8(Clause, {
        children: [
          /* @__PURE__ */ jsx8(Select8, {
            value: selectedEmail,
            options: customEmailOptions,
            onChange: handleEmailChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx8(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx8(Select8, {
              value: selectedEvent,
              options: EMAIL_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs8(Button3, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx8(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleChange,
        disableTimes: !event || !EVENTS_WITH_TIMES.has(event)
      })
    ]
  });
};

// src/components/EmbedsClause.tsx
import { useContext as useContext5, useMemo as useMemo3 } from "react";
import { Select as Select9, Button as Button4 } from "@studio/legacy-components";
import { jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var PAGE_KEY3 = "embeds-clause";
var EMBED_EVENT_OPTIONS = [
  {
    label: "has been seen",
    value: "appcues:v2:experience_started",
    negation: false
  },
  {
    label: "has not been seen",
    value: "appcues:v2:experience_started:negated",
    negation: true
  },
  {
    label: "has been completed",
    value: "appcues:v2:experience_completed",
    negation: false
  },
  {
    label: "has not been completed",
    value: "appcues:v2:experience_completed:negated",
    negation: true
  },
  {
    label: "has been dismissed",
    value: "appcues:v2:experience_dismissed",
    negation: false
  },
  {
    label: "has not been dismissed",
    value: "appcues:v2:experience_dismissed:negated",
    negation: true
  }
];
var parseEmbedConditions = (conditions) => {
  const { experience } = conditions;
  if (experience.event) {
    return conditions;
  }
  return {
    experience: {
      event: "appcues:v2:experience_started",
      experience: experience.experience,
      type: "embeds"
    }
  };
};
var EmbedsClause = ({
  conditions,
  negated,
  onChange
}) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY3);
  const parsedConditions = useMemo3(
    () => parseEmbedConditions(conditions),
    [conditions]
  );
  const {
    experience: { experience, operator, value, event, times = 1 },
    negation = negated
  } = parsedConditions;
  const { embedOptions = [] } = useContext5(ConditionsContext);
  const options = [...embedOptions];
  if (experience && !options.some(({ value: experienceId }) => experienceId === experience)) {
    options.push({ label: experience, value: experience });
  }
  const selectedEvent = EMBED_EVENT_OPTIONS.find(
    ({ value: eventValue, negation: eventNegation }) => {
      const [eventType] = eventValue.split(":negated");
      return eventType === event && negation === eventNegation;
    }
  );
  const handleChange = (changes, nextNegated = negation) => {
    onChange(
      {
        experience: {
          ...parsedConditions.experience,
          ...changes
        }
      },
      nextNegated
    );
  };
  const handleExperienceChange = ({
    value: newExperience
  }) => handleChange({ experience: newExperience });
  const handleEventChange = ({
    value: eventValue,
    negation: eventNegation
  }) => {
    const [eventType] = eventValue.split(":negated");
    handleChange({ event: eventType }, eventNegation);
  };
  return /* @__PURE__ */ jsxs9(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs9(Clause, {
        children: [
          /* @__PURE__ */ jsx9(Select9, {
            value: options.find(
              ({ value: optionExperience }) => optionExperience === experience
            ),
            options,
            onChange: handleExperienceChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx9(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx9(Select9, {
              value: selectedEvent,
              options: EMBED_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs9(Button4, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx9(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleChange
      })
    ]
  });
};

// src/components/EventClause.tsx
import { useContext as useContext6, useMemo as useMemo4 } from "react";
import { Select as Select10, Button as Button5 } from "@studio/legacy-components";
import { jsx as jsx10, jsxs as jsxs10 } from "react/jsx-runtime";
var NEGATED_OPTIONS = [
  { label: "has occurred", value: false },
  { label: "has not occurred", value: true }
];
function formatEventName(event) {
  return event.replace("appcues_custom:", "");
}
function labelForEvent(event) {
  return isKeyMomentEvent(event) ? event.slice(KEY_MOMENT_EVENT_PREFIX.length) : formatEventName(event);
}
var PAGE_KEY4 = "event-clause";
var EventClause = ({
  conditions,
  negated,
  isKeyMoment: isKeyMoment2 = false,
  onChange
}) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY4);
  const {
    event: { event, operator, times = 1, value = 1 }
  } = conditions;
  const { eventOptions, keyMomentOptions = [] } = useContext6(ConditionsContext);
  const handleChange = (changes, nextNegated = negated) => {
    onChange({ event: { ...conditions.event, ...changes } }, nextNegated);
  };
  const handleEventChange = ({ value: newEvent }) => handleChange({ event: newEvent });
  const handleNegatedChange = ({ value: nextNegated }) => handleChange({}, nextNegated);
  const hasValue = Boolean(event) && event !== KEY_MOMENT_EVENT_PREFIX;
  const options = useMemo4(() => {
    const available = isKeyMoment2 ? keyMomentOptions : eventOptions.map((option) => ({
      ...option,
      label: formatEventName(option.label)
    }));
    const isKnownEvent = available.some(
      ({ value: optionEvent }) => optionEvent === event
    );
    if (hasValue && !isKnownEvent) {
      return [...available, { label: labelForEvent(event), value: event }];
    }
    return available;
  }, [event, eventOptions, hasValue, isKeyMoment2, keyMomentOptions]);
  const selectedEvent = useMemo4(
    () => hasValue ? options.find(({ value: optionEvent }) => optionEvent === event) : void 0,
    [event, hasValue, options]
  );
  return /* @__PURE__ */ jsxs10(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs10(Clause, {
        children: [
          /* @__PURE__ */ jsx10(Select10, {
            value: selectedEvent,
            options,
            onChange: handleEventChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx10(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx10(Select10, {
              value: negated ? NEGATED_OPTIONS[1] : NEGATED_OPTIONS[0],
              options: NEGATED_OPTIONS,
              onChange: handleNegatedChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs10(Button5, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx10(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleChange
      })
    ]
  });
};

// src/components/GroupsClause.tsx
import { useContext as useContext8 } from "react";
import {
  Select as Select11,
  Input as Input4,
  Button as Button6,
  TextArea as TextArea3,
  DataList as DataList2,
  DateInput as DateInput3
} from "@studio/legacy-components";

// src/components/constants/datetime.js
var MILLISECONDS_PER_DAY2 = 24 * 60 * 60 * 1e3;

// src/components/use-property-conditions.js
import { useState as useState5, useEffect as useEffect2 } from "react";
import { startOf as startOf2 } from "@studio/legacy-components";
var use_property_conditions_default = (innerConditions, onChange) => {
  const { operator, value } = innerConditions;
  const hasOptions = MULTI_VALUE_OPERATORS.has(operator);
  const isRelativeTimeOperator = RELATIVE_TIME_OPERATORS.has(operator);
  const isAbsoluteTimeOperator = ABSOLUTE_TIME_OPERATORS.has(operator);
  const isNumericOperator = NUMERIC_OPERATORS.has(operator);
  const isDefaultOperator = DEFAULT_OPERATORS.has(operator);
  const isMultiValueOperator = MULTI_VALUE_OPERATORS.has(operator);
  const isExistentialOperator = EXISTENTIAL_OPERATORS.has(operator);
  const [draftValue, setDraftValue] = useState5(value);
  const handleCurrentValueChange = ({ target: { value: nextValue } }) => {
    if (isMultiValueOperator) {
      setDraftValue(
        nextValue.replace(/(\s*,\s*)|\n/g, ",\n").replace(/\s*,\s*/g, ",").replace(/(^[\t ]*\n*\s+$)/gm, "").split(",").join("\n")
      );
    } else {
      setDraftValue(nextValue);
    }
  };
  useEffect2(() => {
    if (isRelativeTimeOperator) {
      setDraftValue(value / MILLISECONDS_PER_DAY2);
    } else {
      setDraftValue(value);
    }
  }, [isRelativeTimeOperator, value]);
  const handlePropertyChange = ({ value: newProperty }) => void onChange({ ...innerConditions, property: newProperty });
  const handleOperatorChange = ({ value: newOperator }) => {
    let nextValue = value;
    if (MULTI_VALUE_OPERATORS.has(newOperator) && !isMultiValueOperator) {
      nextValue = "";
    }
    if (NUMERIC_OPERATORS.has(newOperator) && !isNumericOperator) {
      nextValue = 1;
    }
    if (RELATIVE_TIME_OPERATORS.has(newOperator) && !isRelativeTimeOperator) {
      nextValue = 24 * 60 * 60 * 1e3;
    }
    if (ABSOLUTE_TIME_OPERATORS.has(newOperator) && !isAbsoluteTimeOperator) {
      nextValue = startOf2(new Date(), "day").valueOf();
    }
    if (EXISTENTIAL_OPERATORS.has(newOperator)) {
      nextValue = null;
    }
    if (DEFAULT_OPERATORS.has(newOperator) && !isDefaultOperator) {
      nextValue = "";
    }
    onChange({
      ...innerConditions,
      operator: newOperator,
      value: nextValue
    });
  };
  const handleValueAsDateChange = (newValue) => void onChange({
    ...innerConditions,
    value: startOf2(newValue, "day").valueOf()
  });
  const handleValueBlur = ({ target: { value: newValue, valueAsNumber } }) => {
    let nextValue = newValue;
    const isNumber = !Number.isNaN(valueAsNumber) && valueAsNumber !== null && valueAsNumber !== void 0;
    if (isRelativeTimeOperator) {
      const numberOfDays = isNumber ? valueAsNumber : 1;
      nextValue = numberOfDays * MILLISECONDS_PER_DAY2;
    }
    if (isNumericOperator) {
      nextValue = isNumber ? valueAsNumber : 1;
    }
    onChange({ ...innerConditions, value: nextValue });
  };
  const handleValueSelected = ({ selectedItem }) => void onChange({ ...innerConditions, value: selectedItem });
  return {
    draftValue,
    hasOptions,
    isRelativeTimeOperator,
    isAbsoluteTimeOperator,
    isNumericOperator,
    isDefaultOperator,
    isMultiValueOperator,
    isExistentialOperator,
    onPropertyChange: handlePropertyChange,
    onOperatorChange: handleOperatorChange,
    onValueChange: handleCurrentValueChange,
    onDateChange: handleValueAsDateChange,
    onValueBlur: handleValueBlur,
    onValueSelected: handleValueSelected
  };
};

// src/components/use-suggestions.ts
import { useContext as useContext7, useState as useState7, useEffect as useEffect4 } from "react";

// src/components/use-debounce.ts
import { useState as useState6, useEffect as useEffect3 } from "react";
function useDebounce(value, milliSeconds) {
  const [debouncedValue, setDebouncedValue] = useState6(value);
  useEffect3(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, milliSeconds);
    return () => {
      clearTimeout(handler);
    };
  }, [value, milliSeconds]);
  return debouncedValue;
}

// src/components/use-suggestions.ts
function useSuggestions({
  type,
  property,
  term,
  disabled
}) {
  const { onAttributeChange } = useContext7(ConditionsContext);
  const [initialAttributeName] = useState7(property);
  const [initialTerm] = useState7(term);
  const hasChanged = property !== initialAttributeName || term !== initialTerm;
  const [suggestions, setSuggestions] = useState7([]);
  const [fetching, setFetching] = useState7(false);
  const debouncedTerm = useDebounce(term, 500);
  const shouldFetch = !disabled && (hasChanged || term === "");
  useEffect4(() => {
    (async () => {
      if (shouldFetch) {
        setFetching(true);
        setSuggestions(
          await (onAttributeChange == null ? void 0 : onAttributeChange(type, property, debouncedTerm))
        );
        setFetching(false);
      }
    })();
  }, [onAttributeChange, shouldFetch, property, debouncedTerm]);
  return [suggestions, fetching];
}

// src/components/GroupsClause.tsx
import { Fragment as Fragment3, jsx as jsx11, jsxs as jsxs11 } from "react/jsx-runtime";
var PAGE_KEY5 = "groups-clause";
var GroupsClause = ({ conditions, negated, onChange }) => {
  if (negated) {
    throw new Error("Groups conditions cannot be negated");
  }
  const { groups: innerConditions } = conditions;
  const { property, operator, value } = innerConditions;
  const { groupOptions } = useContext8(ConditionsContext);
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY5);
  const handleChange = (newInnerConditions) => onChange({ groups: newInnerConditions });
  const {
    draftValue,
    isRelativeTimeOperator,
    isAbsoluteTimeOperator,
    isNumericOperator,
    isDefaultOperator,
    isMultiValueOperator,
    onPropertyChange,
    onOperatorChange,
    onValueChange,
    onDateChange,
    onValueBlur,
    onValueSelected
  } = use_property_conditions_default(innerConditions, handleChange);
  const options = [...groupOptions];
  if (property && !options.some(
    ({ value: propertyName }) => propertyName === property
  )) {
    options.push({
      label: property,
      value: property
    });
  }
  const selectedPropertyOption = options.find(
    (option) => option.value === property
  );
  const selectedOperatorOption = ALL_OPERATORS.find(
    ({ value: op }) => op === operator
  );
  const [suggestions, fetching] = useSuggestions({
    type: "GROUP",
    property,
    term: draftValue,
    disabled: !property || !isDefaultOperator
  });
  return /* @__PURE__ */ jsxs11(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs11(Clause, {
        children: [
          /* @__PURE__ */ jsx11(Select11, {
            name: "property",
            value: selectedPropertyOption,
            options,
            onChange: onPropertyChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx11(Select11, {
            name: "operator",
            value: selectedOperatorOption,
            options: ALL_OPERATORS,
            onChange: onOperatorChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          isRelativeTimeOperator && /* @__PURE__ */ jsxs11(Fragment3, {
            children: [
              /* @__PURE__ */ jsx11(QualificationWrapper, {
                conditions,
                children: /* @__PURE__ */ jsx11(Input4, {
                  type: "number",
                  "aria-label": "value",
                  name: "value",
                  value: draftValue,
                  onChange: onValueChange,
                  onBlur: onValueBlur
                })
              }),
              /* @__PURE__ */ jsxs11("span", {
                children: [
                  draftValue === MILLISECONDS_PER_DAY2 ? "day" : "days",
                  ["> ago", "< ago"].includes(operator) && " ago"
                ]
              })
            ]
          }),
          isAbsoluteTimeOperator && /* @__PURE__ */ jsx11(DateInput3, {
            future: true,
            value: value ? new Date(value) : Date.now(),
            onChange: onDateChange
          }),
          isNumericOperator && /* @__PURE__ */ jsx11(QualificationWrapper, {
            conditions,
            children: /* @__PURE__ */ jsx11(Input4, {
              type: "number",
              "aria-label": "value",
              name: "value",
              value: draftValue,
              onChange: onValueChange,
              onBlur: onValueBlur
            })
          }),
          isDefaultOperator && /* @__PURE__ */ jsx11(QualificationWrapper, {
            conditions,
            children: /* @__PURE__ */ jsx11(DataList2, {
              "aria-label": "value",
              name: "value",
              value: draftValue,
              suggestions,
              loading: fetching,
              onChange: onValueChange,
              onBlur: onValueBlur,
              onSelectedItemChange: onValueSelected
            })
          }),
          isMultiValueOperator && /* @__PURE__ */ jsxs11(Button6, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " values"
            ]
          })
        ]
      }),
      showOptions && isMultiValueOperator && /* @__PURE__ */ jsx11(QualificationWrapper, {
        conditions,
        children: /* @__PURE__ */ jsx11(TextArea3, {
          name: "value",
          "aria-label": "value",
          placeholder: "Enter values separated by commas or line breaks",
          value: draftValue,
          onChange: onValueChange,
          onBlur: onValueBlur
        })
      })
    ]
  });
};

// src/components/LanguagesClause.tsx
import { useContext as useContext9 } from "react";
import { Select as Select12 } from "@studio/legacy-components";
import { jsx as jsx12, jsxs as jsxs12 } from "react/jsx-runtime";
var NEGATED_OPTIONS2 = [
  { label: "is", value: false },
  { label: "isn't", value: true }
];
var LanguagesClause = ({ conditions, negated, onChange }) => {
  const {
    languages: { value }
  } = conditions;
  const { languageOptions } = useContext9(ConditionsContext);
  const handleChange = (changes, nextNegated = negated) => {
    onChange(
      { languages: { ...conditions.languages, ...changes } },
      nextNegated
    );
  };
  const handleLocaleChange = ({ value: locale }) => handleChange({ value: locale });
  const handleNegatedChange = ({ value: nextNegated }) => handleChange({}, nextNegated);
  return /* @__PURE__ */ jsxs12(Clause, {
    children: [
      /* @__PURE__ */ jsx12(Select12, {
        value: negated ? NEGATED_OPTIONS2[1] : NEGATED_OPTIONS2[0],
        options: NEGATED_OPTIONS2,
        onChange: handleNegatedChange,
        portal: true,
        menuShouldBlockScroll: true
      }),
      /* @__PURE__ */ jsx12(QualificationWrapper, {
        conditions,
        negated,
        children: /* @__PURE__ */ jsx12(Select12, {
          value: languageOptions.find(({ value: locale }) => locale === value),
          options: languageOptions,
          onChange: handleLocaleChange,
          portal: true,
          menuShouldBlockScroll: true
        })
      })
    ]
  });
};

// src/components/MobileContentClause.tsx
import { useContext as useContext10, useMemo as useMemo5 } from "react";
import { Select as Select13, Button as Button7 } from "@studio/legacy-components";

// src/components/helpers.js
import Fuzzyurl from "fuzzyurl";

// src/components/constants/language.js
var LANGUAGE_OPTIONS = [
  {
    label: "Abkhaz",
    value: "ab"
  },
  {
    label: "Afar",
    value: "aa"
  },
  {
    label: "Afrikaans",
    value: "af"
  },
  {
    label: "Akan",
    value: "ak"
  },
  {
    label: "Albanian",
    value: "sq"
  },
  {
    label: "Amharic",
    value: "am"
  },
  {
    label: "Arabic",
    value: "ar"
  },
  {
    label: "Arabic - Algeria",
    value: "ar-dz"
  },
  {
    label: "Arabic - Bahrain",
    value: "ar-bh"
  },
  {
    label: "Arabic - Egypt",
    value: "ar-eg"
  },
  {
    label: "Arabic - Iraq",
    value: "ar-iq"
  },
  {
    label: "Arabic - Jordan",
    value: "ar-jo"
  },
  {
    label: "Arabic - Kuwait",
    value: "ar-kw"
  },
  {
    label: "Arabic - Lebanon",
    value: "ar-lb"
  },
  {
    label: "Arabic - Libya",
    value: "ar-ly"
  },
  {
    label: "Arabic - Morocco",
    value: "ar-ma"
  },
  {
    label: "Arabic - Oman",
    value: "ar-om"
  },
  {
    label: "Arabic - Qatar",
    value: "ar-qa"
  },
  {
    label: "Arabic - Saudi Arabia",
    value: "ar-sa"
  },
  {
    label: "Arabic - Syria",
    value: "ar-sy"
  },
  {
    label: "Arabic - Tunisia",
    value: "ar-tn"
  },
  {
    label: "Arabic - United Arab Emirates",
    value: "ar-ae"
  },
  {
    label: "Arabic - Yemen",
    value: "ar-ye"
  },
  {
    label: "Aragonese",
    value: "an"
  },
  {
    label: "Armenian",
    value: "hy"
  },
  {
    label: "Assamese",
    value: "as"
  },
  {
    label: "Avaric",
    value: "av"
  },
  {
    label: "Avestan",
    value: "ae"
  },
  {
    label: "Aymara",
    value: "ay"
  },
  {
    label: "Azerbaijani",
    value: "az"
  },
  {
    label: "Azeri - Cyrillic",
    value: "az-az"
  },
  {
    label: "Azeri - Latin",
    value: "az-az"
  },
  {
    label: "Bambara",
    value: "bm"
  },
  {
    label: "Bashkir",
    value: "ba"
  },
  {
    label: "Basque",
    value: "eu"
  },
  {
    label: "Belarusian",
    value: "be"
  },
  {
    label: "Bengali, Bangla",
    value: "bn"
  },
  {
    label: "Bihari",
    value: "bh"
  },
  {
    label: "Bislama",
    value: "bi"
  },
  {
    label: "Bosnian",
    value: "bs"
  },
  {
    label: "Breton",
    value: "br"
  },
  {
    label: "Bulgarian",
    value: "bg"
  },
  {
    label: "Burmese",
    value: "my"
  },
  {
    label: "Catalan",
    value: "ca"
  },
  {
    label: "Chamorro",
    value: "ch"
  },
  {
    label: "Chechen",
    value: "ce"
  },
  {
    label: "Chichewa, Chewa, Nyanja",
    value: "ny"
  },
  {
    label: "Chinese",
    value: "zh"
  },
  {
    label: "Chinese - China",
    value: "zh-cn"
  },
  {
    label: "Chinese - Hong Kong SAR",
    value: "zh-hk"
  },
  {
    label: "Chinese - Macau SAR",
    value: "zh-mo"
  },
  {
    label: "Chinese - Singapore",
    value: "zh-sg"
  },
  {
    label: "Chinese - Taiwan",
    value: "zh-tw"
  },
  {
    label: "Chinese (Simplified)",
    value: "zh-hans"
  },
  {
    label: "Chinese (Simplified) - China",
    value: "zh-hans-cn"
  },
  {
    label: "Chinese (Simplified) - Hong Kong SAR",
    value: "zh-hans-hk"
  },
  {
    label: "Chinese (Simplified) - Macao SAR",
    value: "zh-hans-mo"
  },
  {
    label: "Chinese (Simplified) - Singapore",
    value: "zh-hans-sg"
  },
  {
    label: "Chinese (Traditional)",
    value: "zh-hant"
  },
  {
    label: "Chinese (Traditional) - Hong Kong SAR",
    value: "zh-hant-hk"
  },
  {
    label: "Chinese (Traditional) - Macao SAR",
    value: "zh-hant-mo"
  },
  {
    label: "Chinese (Traditional) - Taiwan",
    value: "zh-hant-tw"
  },
  {
    label: "Chuvash",
    value: "cv"
  },
  {
    label: "Cornish",
    value: "kw"
  },
  {
    label: "Corsican",
    value: "co"
  },
  {
    label: "Cree",
    value: "cr"
  },
  {
    label: "Croatian",
    value: "hr"
  },
  {
    label: "Czech",
    value: "cs"
  },
  {
    label: "Danish",
    value: "da"
  },
  {
    label: "Divehi, Dhivehi, Maldivian",
    value: "dv"
  },
  {
    label: "Dutch",
    value: "nl"
  },
  {
    label: "Dutch - Belgium",
    value: "nl-be"
  },
  {
    label: "Dutch - Netherlands",
    value: "nl-nl"
  },
  {
    label: "Dzongkha",
    value: "dz"
  },
  {
    label: "English",
    value: "en"
  },
  {
    label: "English - Australia",
    value: "en-au"
  },
  {
    label: "English - Belize",
    value: "en-bz"
  },
  {
    label: "English - Canada",
    value: "en-ca"
  },
  {
    label: "English - Caribbean",
    value: "en-cb"
  },
  {
    label: "English - Great Britain",
    value: "en-gb"
  },
  {
    label: "English - India",
    value: "en-in"
  },
  {
    label: "English - Ireland",
    value: "en-ie"
  },
  {
    label: "English - Jamaica",
    value: "en-jm"
  },
  {
    label: "English - New Zealand",
    value: "en-nz"
  },
  {
    label: "English - Phillippines",
    value: "en-ph"
  },
  {
    label: "English - Southern Africa",
    value: "en-za"
  },
  {
    label: "English - Trinidad",
    value: "en-tt"
  },
  {
    label: "English - United States",
    value: "en-us"
  },
  {
    label: "Esperanto",
    value: "eo"
  },
  {
    label: "Estonian",
    value: "et"
  },
  {
    label: "Ewe",
    value: "ee"
  },
  {
    label: "Faroese",
    value: "fo"
  },
  {
    label: "Fijian",
    value: "fj"
  },
  {
    label: "Filipino",
    value: "fil-ph"
  },
  {
    label: "Finnish",
    value: "fi"
  },
  {
    label: "French",
    value: "fr"
  },
  {
    label: "French - Belgium",
    value: "fr-be"
  },
  {
    label: "French - Canada",
    value: "fr-ca"
  },
  {
    label: "French - France",
    value: "fr-fr"
  },
  {
    label: "French - Luxembourg",
    value: "fr-lu"
  },
  {
    label: "French - Switzerland",
    value: "fr-ch"
  },
  {
    label: "Fula, Fulah, Pulaar, Pular",
    value: "ff"
  },
  {
    label: "Galician",
    value: "gl"
  },
  {
    label: "Gaelic - Ireland",
    value: "gd-ie"
  },
  {
    label: "Ganda",
    value: "lg"
  },
  {
    label: "Georgian",
    value: "ka"
  },
  {
    label: "German",
    value: "de"
  },
  {
    label: "German - Austria",
    value: "de-at"
  },
  {
    label: "German - Germany",
    value: "de-de"
  },
  {
    label: "German - Liechtenstein",
    value: "de-li"
  },
  {
    label: "German - Luxembourg",
    value: "de-lu"
  },
  {
    label: "German - Switzerland",
    value: "de-ch"
  },
  {
    label: "Greek (modern)",
    value: "el"
  },
  {
    label: "Guaran\xED",
    value: "gn"
  },
  {
    label: "Gujarati",
    value: "gu"
  },
  {
    label: "Haitian, Haitian Creole",
    value: "ht"
  },
  {
    label: "Hausa",
    value: "ha"
  },
  {
    label: "Hebrew (modern)",
    value: "he"
  },
  {
    label: "Herero",
    value: "hz"
  },
  {
    label: "Hindi",
    value: "hi"
  },
  {
    label: "Hiri Motu",
    value: "ho"
  },
  {
    label: "Hungarian",
    value: "hu"
  },
  {
    label: "Icelandic",
    value: "is"
  },
  {
    label: "Ido",
    value: "io"
  },
  {
    label: "Igbo",
    value: "ig"
  },
  {
    label: "Indonesian",
    value: "id"
  },
  {
    label: "Interlingua",
    value: "ia"
  },
  {
    label: "Interlingue",
    value: "ie"
  },
  {
    label: "Inuktitut",
    value: "iu"
  },
  {
    label: "Inupiaq",
    value: "ik"
  },
  {
    label: "Irish",
    value: "ga"
  },
  {
    label: "Italian",
    value: "it"
  },
  {
    label: "Italian - Italy",
    value: "it-it"
  },
  {
    label: "Italian - Switzerland",
    value: "it-ch"
  },
  {
    label: "Japanese",
    value: "ja"
  },
  {
    label: "Javanese",
    value: "jv"
  },
  {
    label: "Kalaallisut, Greenlandic",
    value: "kl"
  },
  {
    label: "Kannada",
    value: "kn"
  },
  {
    label: "Kanuri",
    value: "kr"
  },
  {
    label: "Kashmiri",
    value: "ks"
  },
  {
    label: "Kazakh",
    value: "kk"
  },
  {
    label: "Khmer",
    value: "km"
  },
  {
    label: "Kikuyu, Gikuyu",
    value: "ki"
  },
  {
    label: "Kinyarwanda",
    value: "rw"
  },
  {
    label: "Kirundi",
    value: "rn"
  },
  {
    label: "Komi",
    value: "kv"
  },
  {
    label: "Kongo",
    value: "kg"
  },
  {
    label: "Korean",
    value: "ko"
  },
  {
    label: "Kurdish",
    value: "ku"
  },
  {
    label: "Kwanyama, Kuanyama",
    value: "kj"
  },
  {
    label: "Kyrgyz",
    value: "ky"
  },
  {
    label: "Lao",
    value: "lo"
  },
  {
    label: "Latin",
    value: "la"
  },
  {
    label: "Latvian",
    value: "lv"
  },
  {
    label: "Limburgish, Limburgan, Limburger",
    value: "li"
  },
  {
    label: "Lingala",
    value: "ln"
  },
  {
    label: "Lithuanian",
    value: "lt"
  },
  {
    label: "Luba-Katanga",
    value: "lu"
  },
  {
    label: "Luxembourgish, Letzeburgesch",
    value: "lb"
  },
  {
    label: "Macedonian",
    value: "mk"
  },
  {
    label: "Malagasy",
    value: "mg"
  },
  {
    label: "Malay",
    value: "ms"
  },
  {
    label: "Malay - Brunei",
    value: "ms-bn"
  },
  {
    label: "Malay - Malaysia",
    value: "ms-my"
  },
  {
    label: "Malayalam",
    value: "ml"
  },
  {
    label: "Maltese",
    value: "mt"
  },
  {
    label: "Manx",
    value: "gv"
  },
  {
    label: "Marathi (Mar\u0101\u1E6Dh\u012B)",
    value: "mr"
  },
  {
    label: "Marshallese",
    value: "mh"
  },
  {
    label: "Mongolian",
    value: "mn"
  },
  {
    label: "Montenegrin",
    value: "me"
  },
  {
    label: "M\u0101ori",
    value: "mi"
  },
  {
    label: "Nauruan",
    value: "na"
  },
  {
    label: "Navajo, Navaho",
    value: "nv"
  },
  {
    label: "Ndonga",
    value: "ng"
  },
  {
    label: "Nepali",
    value: "ne"
  },
  {
    label: "Northern Ndebele",
    value: "nd"
  },
  {
    label: "Northern Sami",
    value: "se"
  },
  {
    label: "Norwegian",
    value: "no"
  },
  {
    label: "Norwegian - Bokml",
    value: "no-no"
  },
  {
    label: "Norwegian - Nynorsk",
    value: "no-no"
  },
  {
    label: "Norwegian Bokm\xE5l",
    value: "nb"
  },
  {
    label: "Norwegian Nynorsk",
    value: "nn"
  },
  {
    label: "Nuosu",
    value: "ii"
  },
  {
    label: "Occitan",
    value: "oc"
  },
  {
    label: "Ojibwe, Ojibwa",
    value: "oj"
  },
  {
    label: "Old Church Slavonic, Church Slavonic,Old Bulgarian",
    value: "cu"
  },
  {
    label: "Oriya",
    value: "or"
  },
  {
    label: "Oromo",
    value: "om"
  },
  {
    label: "Ossetian, Ossetic",
    value: "os"
  },
  {
    label: "Panjabi, Punjabi",
    value: "pa"
  },
  {
    label: "Pashto, Pushto",
    value: "ps"
  },
  {
    label: "Persian (Farsi)",
    value: "fa"
  },
  {
    label: "Polish",
    value: "pl"
  },
  {
    label: "Portuguese",
    value: "pt"
  },
  {
    label: "Portuguese - Brazil",
    value: "pt-br"
  },
  {
    label: "Portuguese - Portugal",
    value: "pt-pt"
  },
  {
    label: "P\u0101li",
    value: "pi"
  },
  {
    label: "Quechua",
    value: "qu"
  },
  {
    label: "Romanian",
    value: "ro"
  },
  {
    label: "Romanian - Moldova",
    value: "ro-mo"
  },
  {
    label: "Romansh",
    value: "rm"
  },
  {
    label: "Russian",
    value: "ru"
  },
  {
    label: "Russian - Moldova",
    value: "ru-mo"
  },
  {
    label: "Samoan",
    value: "sm"
  },
  {
    label: "Sango",
    value: "sg"
  },
  {
    label: "Sanskrit (Sa\u1E41sk\u1E5Bta)",
    value: "sa"
  },
  {
    label: "Sardinian",
    value: "sc"
  },
  {
    label: "Scottish Gaelic, Gaelic",
    value: "gd"
  },
  {
    label: "Serbian",
    value: "sr"
  },
  {
    label: "Serbian (Cyrillic)",
    value: "sr-cyrl"
  },
  {
    label: "Serbian (Cyrillic) - Bosnia and Herzegovina",
    value: "sr-cyrl-ba"
  },
  {
    label: "Serbian (Cyrillic) - Montenegro",
    value: "sr-cyrl-me"
  },
  {
    label: "Serbian (Cyrillic-) - Serbia",
    value: "sr-cyrl-rs"
  },
  {
    label: "Serbian (Latin)",
    value: "sr-latn"
  },
  {
    label: "Serbian (Latin) - Bosnia and Herzegovina",
    value: "sr-latn-ba"
  },
  {
    label: "Serbian (Latin) - Montenegro",
    value: "sr-latn-me"
  },
  {
    label: "Serbian (Latin) - Serbia",
    value: "sr-latn-rs"
  },
  {
    label: "Shona",
    value: "sn"
  },
  {
    label: "Sindhi",
    value: "sd"
  },
  {
    label: "Sinhala, Sinhalese",
    value: "si"
  },
  {
    label: "Slovak",
    value: "sk"
  },
  {
    label: "Slovene",
    value: "sl"
  },
  {
    label: "Somali",
    value: "so"
  },
  {
    label: "Southern Ndebele",
    value: "nr"
  },
  {
    label: "Southern Sotho",
    value: "st"
  },
  {
    label: "Spanish",
    value: "es"
  },
  {
    label: "Spanish - Argentina",
    value: "es-ar"
  },
  {
    label: "Spanish - Bolivia",
    value: "es-bo"
  },
  {
    label: "Spanish - Chile",
    value: "es-cl"
  },
  {
    label: "Spanish - Colombia",
    value: "es-co"
  },
  {
    label: "Spanish - Costa Rica",
    value: "es-cr"
  },
  {
    label: "Spanish - Dominican Republic",
    value: "es-do"
  },
  {
    label: "Spanish - Ecuador",
    value: "es-ec"
  },
  {
    label: "Spanish - El Salvador",
    value: "es-sv"
  },
  {
    label: "Spanish - Guatemala",
    value: "es-gt"
  },
  {
    label: "Spanish - Honduras",
    value: "es-hn"
  },
  {
    label: "Spanish - Mexico",
    value: "es-mx"
  },
  {
    label: "Spanish - Nicaragua",
    value: "es-ni"
  },
  {
    label: "Spanish - Panama",
    value: "es-pa"
  },
  {
    label: "Spanish - Paraguay",
    value: "es-py"
  },
  {
    label: "Spanish - Peru",
    value: "es-pe"
  },
  {
    label: "Spanish - Puerto Rico",
    value: "es-pr"
  },
  {
    label: "Spanish - Spain (Traditional)",
    value: "es-es"
  },
  {
    label: "Spanish - Uruguay",
    value: "es-uy"
  },
  {
    label: "Spanish - Venezuela",
    value: "es-ve"
  },
  {
    label: "Sundanese",
    value: "su"
  },
  {
    label: "Swahili",
    value: "sw"
  },
  {
    label: "Swati",
    value: "ss"
  },
  {
    label: "Swedish",
    value: "sv"
  },
  {
    label: "Swedish - Finland",
    value: "sv-fi"
  },
  {
    label: "Swedish - Sweden",
    value: "sv-se"
  },
  {
    label: "Tagalog",
    value: "tl"
  },
  {
    label: "Tahitian",
    value: "ty"
  },
  {
    label: "Tajik",
    value: "tg"
  },
  {
    label: "Tamil",
    value: "ta"
  },
  {
    label: "Tatar",
    value: "tt"
  },
  {
    label: "Telugu",
    value: "te"
  },
  {
    label: "Thai",
    value: "th"
  },
  {
    label: "Tibetan Standard, Tibetan, Central",
    value: "bo"
  },
  {
    label: "Tigrinya",
    value: "ti"
  },
  {
    label: "Tonga (Tonga Islands)",
    value: "to"
  },
  {
    label: "Tsonga",
    value: "ts"
  },
  {
    label: "Tswana",
    value: "tn"
  },
  {
    label: "Turkish",
    value: "tr"
  },
  {
    label: "Turkmen",
    value: "tk"
  },
  {
    label: "Twi",
    value: "tw"
  },
  {
    label: "Ukrainian",
    value: "uk"
  },
  {
    label: "Urdu",
    value: "ur"
  },
  {
    label: "Uyghur",
    value: "ug"
  },
  {
    label: "Uzbek",
    value: "uz"
  },
  {
    label: "Uzbek - Cyrillic",
    value: "uz-uz"
  },
  {
    label: "Uzbek - Latin",
    value: "uz-uz"
  },
  {
    label: "Venda",
    value: "ve"
  },
  {
    label: "Vietnamese",
    value: "vi"
  },
  {
    label: "Volap\xFCk",
    value: "vo"
  },
  {
    label: "Walloon",
    value: "wa"
  },
  {
    label: "Welsh",
    value: "cy"
  },
  {
    label: "Western Frisian",
    value: "fy"
  },
  {
    label: "Wolof",
    value: "wo"
  },
  {
    label: "Xhosa",
    value: "xh"
  },
  {
    label: "Yiddish",
    value: "yi"
  },
  {
    label: "Yoruba",
    value: "yo"
  },
  {
    label: "Zhuang, Chuang",
    value: "za"
  },
  {
    label: "Zulu",
    value: "zu"
  }
];

// src/components/constants/stepTypes.js
var STEP_TYPE_IDS = {
  JOURNEY: "journey",
  SATISFACTION: "satisfaction-survey",
  MODAL: "modal",
  HOTSPOT_GROUP: "hotspot-group",
  TOOLTIP_GROUP: "tooltip-group",
  SLIDEOUT: "shorty",
  REDIRECT: "redirect",
  COACHMARK_GROUP: "coachmark-group",
  CHECKLIST: "checklist",
  WAIT_FOR_PAGE: "wait-for-page",
  UNKNOWN: "unknown"
};

// src/components/schema.js
import { object, string, number, array, lazy, mixed } from "yup";
var URLSchema = object().shape({
  url: object().shape({
    operator: string().oneOf(["==", "!=", "*", "!*", "^", "!^", "$", "!$", "regex"]).required(),
    value: string().when("operator", {
      is: (op) => ["==", "!=", "regex"].includes(op),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required()
    })
  })
});
var DomainsSchema = object().shape({
  domains: object().shape({
    operator: string().matches(/==/).required(),
    value: string().required()
  })
});
var PropertiesInnerSchema = object().shape({
  property: string().required(),
  operator: string().oneOf([
    "==",
    "!=",
    ">",
    "<",
    ">=",
    "<=",
    "*",
    "!*",
    "^",
    "!^",
    "$",
    "!$",
    "?",
    "!?",
    "in",
    "not in",
    "since",
    "> ago",
    "< ago",
    "within",
    "> time",
    "< time",
    "regex"
  ]),
  value: mixed().when("operator", {
    is: (op) => ["?", "!?", "==", "!=", "regex"].includes(op),
    then: (schema) => schema.nullable().notRequired(),
    otherwise: (schema) => schema.when("operator", {
      is: (op) => ["> ago", "< ago", "within", "> time", "< time"].includes(op),
      then: (sch) => sch.matches(/\d+/).required(),
      otherwise: (sch) => sch.required()
    })
  })
});
var PropertiesSchema = object().shape({
  properties: PropertiesInnerSchema
});
var GroupSchema = object().shape({
  group: PropertiesInnerSchema
});
var GroupsSchema = object().shape({
  groups: PropertiesInnerSchema
});
var LanguagesSchema = object().shape({
  languages: object().shape({
    value: string().min(2).max(5).required(),
    operator: string().matches(/\^/).required()
  })
});
var ContentSchema = object().shape({
  content: object().shape({
    content: string().required(),
    operator: string().oneOf(["< ago", "since", ""]),
    times: number(),
    value: number().when("operator", {
      is: (op) => ["< ago", "since"].includes(op),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    })
  })
});
var EventSchema = object().shape({
  event: object().shape({
    event: string().required(),
    times: number(),
    operator: string().oneOf(["< ago", "since"]),
    value: number().when("operator", {
      is: (op) => ["< ago", "since"].includes(op),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    })
  })
});
var EmailSchema = object().shape({
  email: object().shape({
    email: string().required(),
    event: string().oneOf([
      "delivered",
      "spam",
      "failed",
      "skipped",
      "opened",
      "clicked"
    ]),
    operator: string().oneOf(["< ago", "since"]),
    times: number().when("event", {
      is: (value) => ["clicked", "opened"].includes(value),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    value: number().when("operator", {
      is: (op) => ["< ago", "since"].includes(op),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    })
  })
});
var PushSchema = object().shape({
  push: object().shape({
    push: string().required(),
    event: string().oneOf(["sent", "opened", "failed", "skipped"]),
    operator: string().oneOf(["< ago", "since"]),
    value: number().when("operator", {
      is: (op) => ["< ago", "since"].includes(op),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    })
  })
});
var SegmentsSchema = object().shape({
  segments: object().shape({
    segment: string().required()
  })
});
var ChecklistsSchema = object().shape({
  checklists: object().shape({
    checklist: string().required(),
    status: string().oneOf([
      "in_progress",
      "skipped",
      "completed dismissed",
      "completed\ndismissed",
      null
    ]).nullable()
  })
});
var ExperienceSchema = object().shape({
  experience: object().shape({
    experience: string().required(),
    type: string().required()
  })
});
var AttributesSchema = object().shape({
  attributes: object().shape({
    attribute: string().required(),
    operator: string().oneOf([
      "==",
      "!=",
      "*",
      "!*",
      "^",
      "!^",
      "$",
      "!$",
      "?",
      "!?",
      "in",
      "not in",
      ">",
      "<",
      "regex"
    ]),
    value: string().required()
  })
});
var AppSchema = object().shape({
  app: object().shape({
    operator: string().oneOf(["=="]),
    value: string().required()
  })
});
var ScreenSchema = object().shape({
  screen: object().shape({
    operator: string().oneOf([
      "==",
      "!=",
      "*",
      "!*",
      "^",
      "!^",
      "$",
      "!$",
      "regex"
    ]),
    value: string().required()
  })
});
var VersionSchema = object().shape({
  screen: object().shape({
    operator: string().oneOf(["==", ">", "<"]),
    value: string().required()
  })
});
var ExperienceStepSchema = object().shape({
  experienceStep: object().shape({
    experienceStep: string().required(),
    type: string().required()
  })
});
var AndSchema;
var OrSchema;
var NotSchema;
var TriggerSchema;
var ConditionsSchema = mixed().test({
  name: "is-known-clause-type",
  test: (value) => {
    const [key] = Object.keys(value || {});
    const knownSchemas = {
      and: AndSchema,
      or: OrSchema,
      not: NotSchema,
      url: URLSchema,
      domains: DomainsSchema,
      properties: PropertiesSchema,
      group: GroupSchema,
      groups: GroupsSchema,
      languages: LanguagesSchema,
      content: ContentSchema,
      event: EventSchema,
      segments: SegmentsSchema,
      checklists: ChecklistsSchema,
      experience: ExperienceSchema,
      trigger: TriggerSchema,
      attributes: AttributesSchema,
      app: AppSchema,
      screen: ScreenSchema,
      version: VersionSchema,
      experienceStep: ExperienceStepSchema,
      email: EmailSchema,
      push: PushSchema
    };
    if (knownSchemas[key]) {
      return knownSchemas[key].validateSync(value);
    }
    return false;
  },
  message: "${path} is not a recognized clause"
});
TriggerSchema = object().shape({
  trigger: object().shape({
    event: string().required(),
    conditions: lazy(
      (conditions) => conditions ? ConditionsSchema : mixed().notRequired()
    )
  })
});
AndSchema = object().shape({
  and: array().of(ConditionsSchema).required()
});
OrSchema = object().shape({
  or: array().of(ConditionsSchema).required()
});
NotSchema = object().shape({
  not: array().of(ConditionsSchema).required()
});
var schema_default = ConditionsSchema;

// src/components/helpers.js
var AUDIENCES_CLAUSES = [
  "properties",
  "groups",
  "languages",
  "content",
  "mobile",
  "pins",
  "banners",
  "embeds",
  "checklists",
  "event",
  "keyMoment",
  "segments",
  "email",
  "push",
  "workflow",
  "workflowTask"
];
var NON_AUDIENCE_TYPES = [
  "app",
  "trigger",
  "screen",
  "version",
  "url",
  "domains"
];
var getLeafNodes = (conditions = {}) => {
  const { _qualified, ...restConditions } = conditions || {};
  const [clauseType] = Object.keys(restConditions);
  if (["and", "or", "not"].includes(clauseType)) {
    return conditions[clauseType].flatMap(getLeafNodes);
  }
  return Object.keys(conditions != null ? conditions : {}).length > 0 ? conditions : [];
};
var matchDomain = (pattern, domain) => {
  if (!pattern || !domain) {
    return false;
  }
  const patternLower = pattern.toLowerCase().trim();
  const domainLower = domain.toLowerCase().trim();
  if (!patternLower || !domainLower) {
    return false;
  }
  if (patternLower === domainLower) {
    return true;
  }
  try {
    const domainMask = Fuzzyurl.mask({ hostname: patternLower });
    const domainUrl = new Fuzzyurl({ hostname: domainLower });
    if (!domainMask) {
      return false;
    }
    return Fuzzyurl.matches(domainMask, domainUrl);
  } catch (error) {
    console.error("Fuzzy match error:", error);
    return false;
  }
};
var evaluate = (url, conditions) => {
  const [firstKey] = Object.keys(conditions);
  if (["and", "or", "not"].includes(firstKey)) {
    if (firstKey === "and") {
      return conditions[firstKey].every((condition) => {
        return evaluate(url, condition);
      });
    }
    if (firstKey === "or") {
      return conditions[firstKey].some((condition) => {
        return evaluate(url, condition);
      });
    }
    if (firstKey === "not") {
      return !conditions[firstKey].some((condition) => {
        return evaluate(url, condition);
      });
    }
  }
  const { hostname: domain, pathname, search, hash } = new URL(url);
  const urlPath = `${pathname}${search}${hash}`;
  const opertateOn = conditions.url ? urlPath : domain;
  const { operator, value } = conditions.url || conditions.domains;
  if (firstKey === "domains" && operator === "==") {
    const clauseDomain = conditions.domains.value;
    const lowerCaseDomain = domain.toLowerCase();
    return matchDomain(clauseDomain, lowerCaseDomain);
  }
  switch (operator) {
    case "^":
      return opertateOn.startsWith(value);
    case "!^":
      return !opertateOn.startsWith(value);
    case "$":
      return opertateOn.endsWith(value);
    case "!$":
      return !opertateOn.endsWith(value);
    case "*":
      return opertateOn.includes(value);
    case "!*":
      return !opertateOn.includes(value);
    case "!=":
      return opertateOn !== value;
    case "regex":
      try {
        return new RegExp(value).test(opertateOn);
      } catch (e) {
        return false;
      }
    case "==":
    default:
      return opertateOn === value;
  }
};
var hasMatchingSegment = (conditions = {}, segmentId) => {
  var _a;
  const condition = conditions.or || conditions.and || conditions.not;
  if (condition) {
    return condition.some((item) => {
      if (!item)
        return false;
      return hasMatchingSegment(item, segmentId);
    });
  }
  return ((_a = conditions == null ? void 0 : conditions.segments) == null ? void 0 : _a.segment) === segmentId;
};
var getAudienceConditions = (conditions = {}) => getLeafNodes(conditions).filter((node) => {
  const [type] = Object.keys(node);
  return AUDIENCES_CLAUSES.includes(type);
});
var filterImmutableExperienceSections = ({
  and: branches = []
} = {}) => {
  const dismissed = branches.find((branch) => {
    return getLeafNodes(branch).some(
      (node) => {
        var _a;
        return node.experienceDismissed && !((_a = node.experienceDismissed) == null ? void 0 : _a.type);
      }
    );
  });
  const snoozed = branches.find((branch) => {
    return getLeafNodes(branch).some((node) => {
      var _a;
      return node.experience && ((_a = node.experience) == null ? void 0 : _a.event) === "appcues:v2:experience_snoozed";
    });
  });
  const immutableBranches = { dismissed, snoozed };
  return [
    immutableBranches,
    branches.filter(
      (branch) => !Object.values(immutableBranches).includes(branch)
    )
  ];
};
var getPersistentTargetingSections = (conditions) => {
  const [immutableBranches, mutableBranches] = filterImmutableExperienceSections(conditions);
  return {
    url: mutableBranches.find(
      (branch) => getLeafNodes(branch).some(({ url }) => Boolean(url))
    ),
    domains: mutableBranches.find(
      (branch) => getLeafNodes(branch).some(({ domains }) => Boolean(domains))
    ),
    audience: mutableBranches.find(
      (branch) => getLeafNodes(branch).some(({ url, domains }) => !url && !domains)
    ),
    ...immutableBranches
  };
};
var NPS_UI_PROPERTIES = /* @__PURE__ */ new Set([
  "_appcuesSatisfaction_AskMeLaterSelectedAt",
  "_appcuesSatisfaction_NPSLastCollectedAt",
  "_sessionPageviews",
  "_sessionRandomizer"
]);
var SETTINGS_EXPERIENCE_KEYS = /* @__PURE__ */ new Set([
  "experience",
  "event",
  "type",
  "times",
  "_qualified"
]);
var isSettingsShapedNegation = (clause) => {
  const keys = Object.keys(clause);
  if (keys.length !== 1 || !Array.isArray(clause.not) || clause.not.length !== 1)
    return false;
  const [negated] = clause.not;
  const negatedKeys = Object.keys(negated != null ? negated : {});
  if (negatedKeys.length !== 1 || !negated.experience)
    return false;
  return Object.keys(negated.experience).every(
    (key) => SETTINGS_EXPERIENCE_KEYS.has(key)
  );
};
var findSingleNegatedExperienceBranch = (branches, event, requireTimes, experienceId) => branches.find((branch) => {
  const clauses = branch.and || branch.or || [];
  if (clauses.length !== 1)
    return false;
  const [clause] = clauses;
  if (!isSettingsShapedNegation(clause))
    return false;
  const exp = clause.not[0].experience;
  return (exp == null ? void 0 : exp.event) === event && (requireTimes ? (exp == null ? void 0 : exp.times) != null : (exp == null ? void 0 : exp.times) == null) && (experienceId == null || (exp == null ? void 0 : exp.experience) === experienceId);
});
var getFlowsV2TargetingSections = (conditions, type) => {
  const [immutableBranches, mutableBranches] = filterImmutableExperienceSections(conditions);
  const multipleTimesLimit = findSingleNegatedExperienceBranch(
    mutableBranches,
    "appcues:v2:experience_started",
    true
  );
  const stopWhenCompleted = findSingleNegatedExperienceBranch(
    mutableBranches,
    "appcues:v2:experience_completed",
    false
  );
  const remainingBranches = mutableBranches.filter(
    (branch) => branch !== multipleTimesLimit && branch !== stopWhenCompleted
  );
  return {
    trigger: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ trigger }) => Boolean(trigger))
    ),
    url: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ url }) => Boolean(url))
    ),
    domains: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ domains }) => Boolean(domains))
    ),
    audience: remainingBranches.find(
      (branch) => getLeafNodes(branch).every((clause) => {
        var _a;
        return NON_AUDIENCE_TYPES.every((t) => !clause[t]) && (type !== "nps" || !NPS_UI_PROPERTIES.has((_a = clause.properties) == null ? void 0 : _a.property));
      })
    ),
    ...immutableBranches,
    multipleTimesLimit,
    stopWhenCompleted
  };
};
var getSegments = (conditions) => getLeafNodes(conditions).filter(({ segments }) => Boolean(segments));
var getDomains = (conditions) => getLeafNodes(conditions).filter(({ domains }) => Boolean(domains) && domains.operator === "==").map(({ domains: { value } }) => value).filter(Boolean);
var getMobileTargetingSections = (conditions, experienceId, frequency) => {
  const [immutableBranches, mutableBranches] = filterImmutableExperienceSections(conditions);
  const capBranch = findSingleNegatedExperienceBranch(
    mutableBranches,
    "appcues:v2:experience_started",
    true,
    experienceId
  );
  const capIsTheSetting = frequency == null || frequency === "every_time";
  const inertCap = capIsTheSetting ? void 0 : capBranch;
  const stopWhenCompleted = findSingleNegatedExperienceBranch(
    mutableBranches,
    "appcues:v2:experience_completed",
    false,
    experienceId
  );
  const remainingBranches = mutableBranches.filter(
    (branch) => branch !== capBranch && branch !== stopWhenCompleted
  );
  const audience = remainingBranches.find(
    (branch) => getLeafNodes(branch).every(
      (clause) => NON_AUDIENCE_TYPES.every((type) => !clause[type])
    )
  );
  return {
    app: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ app }) => Boolean(app))
    ),
    trigger: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ trigger }) => Boolean(trigger))
    ),
    screen: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ screen }) => Boolean(screen))
    ),
    version: remainingBranches.find(
      (branch) => getLeafNodes(branch).some(({ version }) => Boolean(version))
    ),
    audience: audience != null ? audience : inertCap,
    ...immutableBranches,
    multipleTimesLimit: capIsTheSetting ? capBranch : void 0,
    stopWhenCompleted,
    inertMultipleTimesLimit: audience ? inertCap : void 0
  };
};
function getCompletionEventLabel(stepType) {
  return stepType && [STEP_TYPE_IDS.MODAL, STEP_TYPE_IDS.SLIDEOUT].includes(stepType) ? "seen" : "completed";
}
var parseMobileConditions = (conditions) => {
  const { experience } = conditions;
  if (experience.event) {
    return conditions;
  }
  if (experience.operator_attribute === "state") {
    const stateMap = {
      completed: "appcues:v2:experience_completed",
      started: "appcues:v2:experience_started",
      dismissed: "appcues:v2:experience_dismissed"
    };
    const event = stateMap[experience.value];
    return {
      experience: {
        event,
        experience: experience.experience,
        type: "mobile"
      },
      negation: experience.operator === "!="
    };
  }
  return {
    experience: {
      event: "appcues:v2:experience_started",
      experience: experience.experience,
      type: "mobile"
    }
  };
};
function isValidConditions(conditions) {
  if (!conditions)
    return false;
  try {
    return schema_default.validateSync(conditions);
  } catch (e) {
    return false;
  }
}
var getFlowTargetingSections = ({ and: branches = [] } = {}) => ({
  trigger: branches.find(
    (branch) => getLeafNodes(branch).some(({ trigger }) => Boolean(trigger))
  ),
  url: branches.find(
    (branch) => getLeafNodes(branch).some(({ url }) => Boolean(url))
  ),
  domains: branches.find(
    (branch) => getLeafNodes(branch).some(({ domains }) => Boolean(domains))
  ),
  audience: branches.find(
    (branch) => getLeafNodes(branch).every(
      (clause) => {
        var _a;
        return NON_AUDIENCE_TYPES.every((type) => !clause[type]) && ((_a = clause.properties) == null ? void 0 : _a.property) !== "_ABGroup";
      }
    )
  ),
  ab: branches.find(
    (branch) => getLeafNodes(branch).every(
      (clause) => {
        var _a;
        return ((_a = clause.properties) == null ? void 0 : _a.property) === "_ABGroup";
      }
    )
  )
});

// src/components/MobileContentClause.tsx
import { jsx as jsx13, jsxs as jsxs13 } from "react/jsx-runtime";
var PAGE_KEY6 = "mobile-content-clause";
var MOBILE_EVENT_OPTIONS = [
  {
    label: "has been seen",
    value: "appcues:v2:experience_started",
    negation: false
  },
  {
    label: "has not been seen",
    value: "appcues:v2:experience_started:negated",
    negation: true
  },
  {
    label: "has been completed",
    value: "appcues:v2:experience_completed",
    negation: false
  },
  {
    label: "has not been completed",
    value: "appcues:v2:experience_completed:negated",
    negation: true
  },
  {
    label: "has been dismissed",
    value: "appcues:v2:experience_dismissed",
    negation: false
  },
  {
    label: "has not been dismissed",
    value: "appcues:v2:experience_dismissed:negated",
    negation: true
  }
];
var MobileContentClause = ({
  conditions,
  negated,
  onChange
}) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY6);
  const parsedConditions = useMemo5(
    () => parseMobileConditions(conditions),
    [conditions]
  );
  const {
    experience: { experience, operator, value, event, times = 1 },
    negation = negated
  } = parsedConditions;
  const { mobileFlowOptions } = useContext10(ConditionsContext);
  const options = [...mobileFlowOptions];
  if (experience && !options.some(({ value: experienceId }) => experienceId === experience)) {
    options.push({ label: experience, value: experience });
  }
  const selectedEvent = MOBILE_EVENT_OPTIONS.find(
    ({ value: eventValue, negation: eventNegation }) => {
      const [eventType] = eventValue.split(":negated");
      return eventType === event && negation === eventNegation;
    }
  );
  const handleChange = (changes, nextNegated = negation) => {
    onChange(
      { experience: { ...parsedConditions.experience, ...changes } },
      nextNegated
    );
  };
  const handleExperienceChange = ({
    value: newExperience
  }) => handleChange({ experience: newExperience });
  const handleEventChange = ({
    value: eventValue,
    negation: eventNegation
  }) => {
    const [eventType] = eventValue.split(":negated");
    handleChange({ event: eventType }, eventNegation);
  };
  return /* @__PURE__ */ jsxs13(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs13(Clause, {
        children: [
          /* @__PURE__ */ jsx13(Select13, {
            value: options.find(
              ({ value: optionExperience }) => optionExperience === experience
            ),
            options,
            onChange: handleExperienceChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx13(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx13(Select13, {
              value: selectedEvent,
              options: MOBILE_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs13(Button7, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx13(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleChange
      })
    ]
  });
};

// src/components/PinsClause.tsx
import { useContext as useContext11 } from "react";
import { Select as Select14 } from "@studio/legacy-components";
import { jsx as jsx14, jsxs as jsxs14 } from "react/jsx-runtime";
var NEGATED_OPTIONS3 = [
  { label: "has been interacted with", value: false },
  { label: "has not been interacted with", value: true }
];
var PinsClause = ({ conditions, negated, onChange }) => {
  const {
    experienceStep: { experienceStep }
  } = conditions;
  const { pinOptions } = useContext11(ConditionsContext);
  const handleChange = (changes, nextNegated = negated) => {
    const experienceStepCondition = { ...conditions.experienceStep };
    if (!changes.interaction)
      delete experienceStepCondition.interaction;
    onChange(
      {
        experienceStep: {
          ...experienceStepCondition,
          ...changes
        }
      },
      nextNegated
    );
  };
  const handlePinChange = ({ value: newPin }) => {
    var _a;
    const type = (_a = pinOptions.find((pin) => pin.value === newPin)) == null ? void 0 : _a.type;
    handleChange({
      experienceStep: newPin,
      ...type === "button" && { interaction: "Button Pin Clicked" }
    });
  };
  const handleNegatedChange = ({ value: nextNegated }) => handleChange({}, nextNegated);
  const options = [...pinOptions];
  if (experienceStep && !pinOptions.some(({ value: pinId }) => pinId === experienceStep)) {
    options.push({ label: experienceStep, value: experienceStep });
  }
  return /* @__PURE__ */ jsx14(ClauseWrapper, {
    children: /* @__PURE__ */ jsxs14(Clause, {
      children: [
        /* @__PURE__ */ jsx14(Select14, {
          value: options.find(
            ({ value: experienceValue }) => experienceValue === experienceStep
          ),
          options,
          onChange: handlePinChange,
          portal: true,
          menuShouldBlockScroll: true
        }),
        /* @__PURE__ */ jsx14(QualificationWrapper, {
          conditions,
          negated,
          children: /* @__PURE__ */ jsx14(Select14, {
            value: negated ? NEGATED_OPTIONS3[1] : NEGATED_OPTIONS3[0],
            options: NEGATED_OPTIONS3,
            onChange: handleNegatedChange,
            portal: true,
            menuShouldBlockScroll: true
          })
        })
      ]
    })
  });
};

// src/components/PropertiesClause.tsx
import { useMemo as useMemo6, useContext as useContext12 } from "react";
import {
  Select as Select15,
  Input as Input5,
  Button as Button8,
  DateInput as DateInput4,
  DataList as DataList3
} from "@studio/legacy-components";
import { Stack } from "@appcues/sonar";

// src/types/context.ts
function isGroupList(list) {
  return list.some((option) => "options" in option);
}
function isGroupOption(option) {
  return "options" in option;
}

// src/lib/property.js
var getPropertyDisplayName = (prop) => {
  return prop == null ? void 0 : prop.replace(/^_appcuesForm_/g, "Form Response: ").replace(/^_appcuesSatisfaction_/g, "").replace(/^_/, "").replace(/[+._-]/g, " ").replace(/([a-z]+)([A-Z]+)/g, (m, g1, g2) => {
    return `${g1} ${g2}`;
  }).replace(/(\s?)([A-Z])([a-z]+)/g, (m, g1, g2, g3) => {
    return ` ${g2}${g3}`;
  }).trim().toLowerCase().replace(/\b(id|url|os|ip|eid|fb|crm|uuid|nps)\b/gi, (m, g1) => {
    return `${g1.toUpperCase()}`;
  }).replace(/(\w)(\w*)/g, (m, g1, g2) => {
    return `${g1.toUpperCase()}${g2}`;
  }).trim();
};
var getOptGroup = (propName) => {
  if (!propName) {
    return null;
  }
  if (/^(_deviceType|_operatingSystem|_browser)/.test(propName)) {
    return "device";
  }
  if (propName.startsWith("_appcuesForm_")) {
    return "form";
  }
  if (propName.startsWith("_appcuesSatisfaction_")) {
    return "satisfaction";
  }
  if (propName.startsWith("_")) {
    return "auto";
  }
  return "normal";
};
var groupProfileAttributes = (attributes) => {
  return attributes.reduce(
    (acc, attribute) => {
      const group = getOptGroup(attribute.value);
      if (group) {
        acc[group].options.push(attribute);
      } else {
        acc.normal.options.push(attribute);
      }
      return acc;
    },
    {
      normal: {
        label: "CUSTOM PROPERTIES",
        options: []
      },
      form: {
        label: "FORM RESPONSES",
        options: []
      },
      device: {
        label: "DEVICE PROPERTIES",
        options: []
      },
      satisfaction: {
        label: "USER SATISFACTION",
        options: []
      },
      auto: {
        label: "AUTO-PROPERTIES",
        options: []
      }
    }
  );
};

// src/components/PropertiesClause.tsx
import { Fragment as Fragment4, jsx as jsx15, jsxs as jsxs15 } from "react/jsx-runtime";
var PAGE_KEY7 = "properties-clause";
var PropertiesClause = ({
  conditions,
  negated,
  propertiesClauseOperators = ALL_OPERATORS,
  onChange
}) => {
  if (negated) {
    throw new Error("Properties conditions cannot be negated");
  }
  const { properties: innerConditions } = conditions;
  const { property, operator, value } = innerConditions;
  const { propertyOptions } = useContext12(ConditionsContext);
  const [showOptions, toggleExpanded] = useStorageToggle(PAGE_KEY7);
  const handleChange = (newInnerConditions) => onChange({ properties: newInnerConditions });
  const {
    draftValue,
    isRelativeTimeOperator,
    isAbsoluteTimeOperator,
    isNumericOperator,
    isDefaultOperator,
    isMultiValueOperator,
    onPropertyChange,
    onOperatorChange,
    onValueChange,
    onDateChange,
    onValueBlur,
    onValueSelected
  } = use_property_conditions_default(innerConditions, handleChange);
  const normalizedPropertyOptions = useMemo6(() => {
    const list = isGroupList(propertyOptions) ? propertyOptions.flatMap((group) => group.options) : propertyOptions;
    return Object.values(
      groupProfileAttributes(
        list.map((prop) => {
          return {
            value: prop.value,
            label: prop.value === prop.label ? getPropertyDisplayName(prop.value) : prop.label
          };
        })
      )
    );
  }, [propertyOptions]);
  const options = useMemo6(() => {
    const isKnowProperty = normalizedPropertyOptions == null ? void 0 : normalizedPropertyOptions.some((option) => {
      return option.options.some((subOption) => subOption.value === property);
    });
    if (isKnowProperty) {
      return normalizedPropertyOptions;
    }
    return [
      ...normalizedPropertyOptions,
      {
        label: "Unrecognized",
        options: [
          {
            label: getPropertyDisplayName(property),
            value: property
          }
        ]
      }
    ];
  }, [normalizedPropertyOptions, property]);
  const selectedPropertyOption = useMemo6(() => {
    if (!property) {
      return null;
    }
    return options.flatMap((group) => group.options).find((option) => option.value === property);
  }, [options, property]);
  const [suggestions, fetching] = useSuggestions({
    type: "USER",
    property,
    term: draftValue,
    disabled: !property || !isDefaultOperator
  });
  return /* @__PURE__ */ jsxs15(Stack, {
    children: [
      /* @__PURE__ */ jsx15(ClauseWrapper, {
        children: /* @__PURE__ */ jsxs15(Clause, {
          children: [
            /* @__PURE__ */ jsx15(Select15, {
              name: "property",
              value: selectedPropertyOption,
              options,
              onChange: onPropertyChange,
              portal: true,
              menuShouldBlockScroll: true
            }),
            /* @__PURE__ */ jsx15(Select15, {
              name: "operator",
              value: propertiesClauseOperators == null ? void 0 : propertiesClauseOperators.find(
                ({ value: op }) => op === operator
              ),
              options: propertiesClauseOperators,
              onChange: onOperatorChange,
              portal: true,
              menuShouldBlockScroll: true
            }),
            isRelativeTimeOperator && /* @__PURE__ */ jsxs15(Fragment4, {
              children: [
                /* @__PURE__ */ jsx15(QualificationWrapper, {
                  conditions,
                  children: /* @__PURE__ */ jsx15(Input5, {
                    type: "number",
                    "aria-label": "value",
                    name: "value",
                    value: draftValue,
                    onChange: onValueChange,
                    onBlur: onValueBlur
                  })
                }),
                /* @__PURE__ */ jsxs15("span", {
                  children: [
                    draftValue === 1 ? "day" : "days",
                    ["> ago", "< ago"].includes(operator) && " ago"
                  ]
                })
              ]
            }),
            isAbsoluteTimeOperator && /* @__PURE__ */ jsx15(QualificationWrapper, {
              conditions,
              children: /* @__PURE__ */ jsx15(DateInput4, {
                portal: true,
                placement: "top",
                future: true,
                value: value ? new Date(Number(value)) : Date.now(),
                onChange: onDateChange
              })
            }),
            isNumericOperator && /* @__PURE__ */ jsx15(QualificationWrapper, {
              conditions,
              children: /* @__PURE__ */ jsx15(Input5, {
                type: "number",
                "aria-label": "value",
                name: "value",
                value: draftValue,
                onChange: onValueChange,
                onBlur: onValueBlur
              })
            }),
            isDefaultOperator && /* @__PURE__ */ jsx15(QualificationWrapper, {
              conditions,
              children: /* @__PURE__ */ jsx15(DataList3, {
                "aria-label": "value",
                name: "value",
                value: draftValue,
                suggestions,
                loading: fetching,
                onBlur: onValueBlur,
                onChange: onValueChange,
                onSelectedItemChange: onValueSelected
              })
            }),
            isMultiValueOperator && /* @__PURE__ */ jsxs15(Button8, {
              kind: "secondary",
              onClick: toggleExpanded,
              children: [
                showOptions ? "Hide" : "Show",
                " values"
              ]
            })
          ]
        })
      }),
      showOptions && isMultiValueOperator && /* @__PURE__ */ jsx15(QualificationWrapper, {
        conditions,
        children: /* @__PURE__ */ jsx15(PropertyTextArea_default, {
          defaultValue: draftValue,
          onChange: onValueChange,
          onBlur: onValueBlur
        })
      })
    ]
  });
};

// src/components/PushClause.tsx
import { useContext as useContext13 } from "react";
import { Select as Select16, Button as Button9 } from "@studio/legacy-components";
import { jsx as jsx16, jsxs as jsxs16 } from "react/jsx-runtime";
var PAGE_KEY8 = "push-clause";
var PUSH_EVENT_OPTIONS = [
  {
    label: "has been sent",
    value: "sent",
    negation: false
  },
  {
    label: "has not been sent",
    value: "sent:negated",
    negation: true
  },
  {
    label: "has been opened",
    value: "opened",
    negation: false
  },
  {
    label: "has not been opened",
    value: "opened:negated",
    negation: true
  },
  {
    label: "failed",
    value: "failed",
    negation: false
  },
  {
    label: "has not failed",
    value: "failed:negated",
    negation: true
  },
  {
    label: "skipped",
    value: "skipped",
    negation: false
  },
  {
    label: "has not skipped",
    value: "skipped:negated",
    negation: true
  }
];
var PushClause = ({ conditions, negated, onChange }) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY8);
  const {
    push: { push, operator, event, value = 1 }
  } = conditions;
  const { pushOptions } = useContext13(ConditionsContext);
  const getDisplayLabel = ({ label, title }) => (label && label !== "Untitled" ? label : title || label).trim();
  const customPushOptions = pushOptions.sort((a, b) => getDisplayLabel(a).localeCompare(getDisplayLabel(b))).map((option) => ({
    ...option,
    label: getDisplayLabel(option)
  }));
  const selectedPush = customPushOptions.find(
    ({ value: pushOption }) => pushOption === push
  );
  const selectedEvent = PUSH_EVENT_OPTIONS.find(
    ({ value: eventValue, negation: eventNegation }) => {
      const [eventType] = eventValue.split(":");
      return eventType === event && negated === eventNegation;
    }
  );
  const handleChange = (changes, nextNegated = negated) => {
    onChange({ push: { ...conditions.push, ...changes } }, nextNegated);
  };
  const handlePushChange = ({ value: newContent }) => handleChange({ push: newContent });
  const handleEventChange = ({
    value: eventValue,
    negation: eventNegation
  }) => {
    const [eventType] = eventValue.split(":");
    handleChange({ event: eventType }, eventNegation);
  };
  return /* @__PURE__ */ jsxs16(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs16(Clause, {
        children: [
          /* @__PURE__ */ jsx16(Select16, {
            value: selectedPush,
            options: customPushOptions,
            onChange: handlePushChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx16(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx16(Select16, {
              value: selectedEvent,
              options: PUSH_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs16(Button9, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx16(TimeframeOptions, {
        operator,
        value,
        onChange: handleChange,
        disableTimes: true
      })
    ]
  });
};

// src/components/ScreenClause.tsx
import { useState as useState8 } from "react";
import { Select as Select17, DataList as DataList4 } from "@studio/legacy-components";
import { jsx as jsx17, jsxs as jsxs17 } from "react/jsx-runtime";
var SCREEN_OPERATORS = [
  OPERATORS.STARTS_WITH,
  OPERATORS.DOESNT_START_WITH,
  OPERATORS.ENDS_WITH,
  OPERATORS.DOESNT_END_WITH,
  OPERATORS.CONTAINS,
  OPERATORS.DOESNT_CONTAIN,
  OPERATORS.EQUALS,
  OPERATORS.DOESNT_EQUAL,
  OPERATORS.MATCHES_REGEX
];
var ScreenClause = ({ conditions, onChange }) => {
  const {
    screen: { operator: currentOperator, value: currentValue = "" }
  } = conditions;
  const [draftValue, setDraftValue] = useState8(currentValue);
  const handleChange = (changes) => void onChange({
    screen: { ...conditions.screen, ...changes }
  });
  const handleOperatorChange = ({
    value: operator
  }) => void handleChange({ operator });
  const handleValueChange = ({
    target: { value: nextValue }
  }) => void setDraftValue(nextValue);
  const handleValueBlur = ({
    target: { value }
  }) => void handleChange({ value });
  const handleValueSelected = ({ selectedItem: value }) => void handleChange({ value });
  const [suggestions, fetching] = useSuggestions({
    type: "USER",
    property: "_lastScreenTitle",
    term: draftValue,
    disabled: false
  });
  return /* @__PURE__ */ jsxs17(Clause, {
    children: [
      /* @__PURE__ */ jsx17(Select17, {
        name: "operator",
        value: SCREEN_OPERATORS.find(({ value }) => value === currentOperator),
        options: SCREEN_OPERATORS,
        onChange: handleOperatorChange,
        portal: true,
        menuShouldBlockScroll: true
      }),
      /* @__PURE__ */ jsx17(DataList4, {
        "aria-label": "value",
        name: "value",
        value: draftValue,
        suggestions,
        loading: fetching,
        onChange: handleValueChange,
        onBlur: handleValueBlur,
        onSelectedItemChange: handleValueSelected
      })
    ]
  });
};

// src/components/SegmentsClause.tsx
import { useContext as useContext14 } from "react";
import { Select as Select18 } from "@studio/legacy-components";
import { jsx as jsx18, jsxs as jsxs18 } from "react/jsx-runtime";
var NEGATED_OPTIONS4 = [
  { label: "matches", value: false },
  { label: "doesn't match", value: true }
];
var SegmentsClause = ({ conditions, negated, onChange }) => {
  const {
    segments: { segment }
  } = conditions;
  const { segmentOptions } = useContext14(ConditionsContext);
  const handleChange = (changes, nextNegated = negated) => {
    onChange({ segments: { ...conditions.segments, ...changes } }, nextNegated);
  };
  const handleSegmentChange = ({ value: newSegment }) => handleChange({ segment: newSegment });
  const handleNegatedChange = ({ value: nextNegated }) => handleChange({}, nextNegated);
  const options = [...segmentOptions];
  if (segment && !options.some(({ value: segmentId }) => segmentId === segment)) {
    options.push({ label: segment, value: segment });
  }
  return /* @__PURE__ */ jsxs18(Clause, {
    children: [
      /* @__PURE__ */ jsx18(Select18, {
        value: options.find(
          ({ value: optionSegment }) => optionSegment === segment
        ),
        options,
        onChange: handleSegmentChange,
        portal: true,
        menuShouldBlockScroll: true
      }),
      /* @__PURE__ */ jsx18(QualificationWrapper, {
        conditions,
        negated,
        children: /* @__PURE__ */ jsx18(Select18, {
          value: negated ? NEGATED_OPTIONS4[1] : NEGATED_OPTIONS4[0],
          options: NEGATED_OPTIONS4,
          onChange: handleNegatedChange,
          portal: true,
          menuShouldBlockScroll: true
        })
      })
    ]
  });
};

// src/components/UrlClause.tsx
import { useRef as useRef3, useEffect as useEffect5 } from "react";
import { Select as Select19, Input as Input6 } from "@studio/legacy-components";
import { jsx as jsx19, jsxs as jsxs19 } from "react/jsx-runtime";
var URL_OPERATORS = [
  OPERATORS.STARTS_WITH,
  OPERATORS.DOESNT_START_WITH,
  OPERATORS.ENDS_WITH,
  OPERATORS.DOESNT_END_WITH,
  OPERATORS.CONTAINS,
  OPERATORS.DOESNT_CONTAIN,
  OPERATORS.EQUALS,
  OPERATORS.DOESNT_EQUAL,
  OPERATORS.MATCHES_REGEX
];
var getPrefixedValue = (operator, value) => {
  const prefixedOperators = [
    OPERATORS.STARTS_WITH,
    OPERATORS.DOESNT_START_WITH,
    OPERATORS.EQUALS,
    OPERATORS.DOESNT_EQUAL
  ].map(({ value: operatorValue }) => operatorValue);
  if (prefixedOperators.includes(operator) && !value.startsWith("/")) {
    return `/${value}`;
  }
  return value;
};
var UrlClause = ({ conditions, onChange }) => {
  const valueRef = useRef3();
  const {
    url: { operator: currentOperator, value: currentValue = "" }
  } = conditions;
  useEffect5(() => {
    if (valueRef.current) {
      valueRef.current.value = currentValue;
    }
  }, [currentValue]);
  const handleChange = (changes) => void onChange({
    url: { ...conditions.url, ...changes }
  });
  const handleOperatorChange = ({
    value: operator
  }) => void handleChange({
    operator,
    value: getPrefixedValue(operator, currentValue)
  });
  const handleValueBlur = ({
    target: { value }
  }) => {
    const prefixedValue = getPrefixedValue(currentOperator, value.trim());
    if (valueRef.current) {
      valueRef.current.value = prefixedValue;
    }
    void handleChange({ value: prefixedValue });
  };
  return /* @__PURE__ */ jsxs19(Clause, {
    children: [
      /* @__PURE__ */ jsx19(Select19, {
        name: "operator",
        value: URL_OPERATORS.find(({ value }) => value === currentOperator),
        options: URL_OPERATORS,
        onChange: handleOperatorChange,
        portal: true,
        menuShouldBlockScroll: true
      }),
      /* @__PURE__ */ jsx19(Input6, {
        type: "text",
        "aria-label": "value",
        name: "value",
        ref: valueRef,
        defaultValue: currentValue,
        placeholder: "e.g. /account",
        onBlur: handleValueBlur
      })
    ]
  });
};

// src/components/VersionClause.tsx
import { useRef as useRef4, useEffect as useEffect6 } from "react";
import { Select as Select20, Input as Input7, WarningNotice as WarningNotice2 } from "@studio/legacy-components";
import { jsx as jsx20, jsxs as jsxs20 } from "react/jsx-runtime";
var VERSION_OPERATORS = [
  OPERATORS.EQUALS,
  OPERATORS.IS_LESS_THAN,
  OPERATORS.IS_LESS_OR_EQUAL,
  OPERATORS.IS_GREATER_THAN,
  OPERATORS.IS_GREATER_OR_EQUAL
];
function semverRegex() {
  return /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/gi;
}
var VersionClause = ({ conditions, onChange }) => {
  const valueRef = useRef4(null);
  const {
    version: { operator: currentOperator, value: currentValue = "" }
  } = conditions;
  const isValidSemver = currentValue ? semverRegex().test(currentValue) : true;
  useEffect6(() => {
    if (valueRef.current) {
      valueRef.current.value = currentValue;
    }
  }, [currentValue]);
  const handleChange = (changes) => void onChange({
    version: { ...conditions.version, ...changes }
  });
  const handleOperatorChange = ({
    value: operator
  }) => void handleChange({ operator });
  const handleValueBlur = ({
    target: { value }
  }) => void handleChange({ value });
  return /* @__PURE__ */ jsxs20(VersionClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs20(Clause, {
        children: [
          /* @__PURE__ */ jsx20(Select20, {
            name: "operator",
            value: VERSION_OPERATORS.find(
              ({ value }) => value === currentOperator
            ),
            options: VERSION_OPERATORS,
            onChange: handleOperatorChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx20(Input7, {
            type: "text",
            "aria-label": "value",
            name: "value",
            ref: valueRef,
            defaultValue: currentValue,
            placeholder: "e.g. 1.0.0",
            onBlur: handleValueBlur
          })
        ]
      }),
      !isValidSemver && /* @__PURE__ */ jsx20(WarningNotice2, {
        children: "The version format should follow semantic versioning: major.minor.patch, or 1.10.0 for example. This Flow may not be shown to users."
      })
    ]
  });
};

// src/components/WorkflowClause.tsx
import { useContext as useContext15 } from "react";
import { Select as Select22, Button as Button10 } from "@studio/legacy-components";

// src/components/RelativeTimeOptions.tsx
import { useState as useState9 } from "react";
import { Select as Select21, Input as Input8, DateInput as DateInput5, startOf as startOf3 } from "@studio/legacy-components";
import { Fragment as Fragment5, jsx as jsx21, jsxs as jsxs21 } from "react/jsx-runtime";
var OPERATOR_OPTIONS2 = [
  { label: "anytime", value: "" },
  { label: "more than", value: "> ago" },
  { label: "in the last", value: "< ago" },
  { label: "after", value: "since" }
];
var MILLISECONDS_PER_DAY3 = 24 * 60 * 60 * 1e3;
function getRelativeTime(operator, value) {
  if (["< ago", "> ago"].includes(operator)) {
    const numDays = value != null ? value : 1;
    return numDays / MILLISECONDS_PER_DAY3;
  }
  return value;
}
var RelativeTimeOptions = ({
  operator = "",
  value,
  onChange
}) => {
  const [relativeTimeValue, setRelativeTimeValue] = useState9(
    getRelativeTime(operator, value)
  );
  const handleOperatorChange = ({
    value: newOperator
  }) => {
    if (["< ago", "> ago"].includes(newOperator)) {
      onChange({ operator: newOperator, value: MILLISECONDS_PER_DAY3 });
    } else if (newOperator === "since") {
      onChange({ operator: newOperator, value: Date.now() });
    } else {
      onChange({ operator: void 0, value: void 0 });
    }
  };
  const handleRelativeTimeChange = ({
    target: { valueAsNumber }
  }) => void setRelativeTimeValue(valueAsNumber);
  const handleRelativeTimeBlur = ({
    target: { valueAsNumber }
  }) => {
    let nextValue = valueAsNumber;
    if (!nextValue) {
      nextValue = 1;
      setRelativeTimeValue(nextValue);
    }
    onChange({
      value: nextValue * MILLISECONDS_PER_DAY3
    });
  };
  const handleDateChange = (date) => void onChange({
    value: startOf3(date, "day").valueOf()
  });
  return /* @__PURE__ */ jsxs21(TimeframeWrapper, {
    children: [
      /* @__PURE__ */ jsx21("span", {
        children: "Is current and last changed"
      }),
      /* @__PURE__ */ jsx21(Select21, {
        name: "operator",
        value: OPERATOR_OPTIONS2.find(
          ({ value: optionOperator }) => optionOperator === operator
        ),
        options: OPERATOR_OPTIONS2,
        onChange: handleOperatorChange,
        portal: true
      }),
      ["> ago", "< ago"].includes(operator) && /* @__PURE__ */ jsxs21(Fragment5, {
        children: [
          /* @__PURE__ */ jsx21(Input8, {
            type: "number",
            name: "value",
            "aria-label": "value",
            value: relativeTimeValue,
            onChange: handleRelativeTimeChange,
            onBlur: handleRelativeTimeBlur
          }),
          /* @__PURE__ */ jsxs21("span", {
            children: [
              relativeTimeValue === 1 ? "day" : "days",
              operator === "> ago" && " ago"
            ]
          })
        ]
      }),
      operator === "since" && /* @__PURE__ */ jsx21(DateInput5, {
        future: true,
        name: "since",
        label: "since",
        value: value ? new Date(value) : new Date(),
        onChange: handleDateChange,
        placement: "top",
        portal: true
      })
    ]
  });
};

// src/components/WorkflowClause.tsx
import { jsx as jsx22, jsxs as jsxs22 } from "react/jsx-runtime";
var PAGE_KEY9 = "workflows-clause";
var WORKFLOW_EVENT_OPTIONS = [
  {
    label: "is enrolled",
    value: "enrolled",
    negation: false
  },
  {
    label: "is unenrolled",
    value: "unenrolled",
    negation: false
  },
  {
    label: "has been entered",
    value: "entered",
    negation: false
  },
  {
    label: "has not been entered",
    value: "entered:negated",
    negation: true
  },
  {
    label: "has been exited",
    value: "exited",
    negation: false
  },
  {
    label: "has not been exited",
    value: "exited:negated",
    negation: true
  }
];
var RELATIVE_EVENTS = /* @__PURE__ */ new Set(["enrolled", "unenrolled"]);
var WorkflowClause = ({ conditions, negated, onChange }) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY9);
  const {
    workflow: { workflow, operator, event, times = 1, value = 1 }
  } = conditions;
  const { workflowOptions } = useContext15(ConditionsContext);
  const selectedWorkflow = workflowOptions.find(
    ({ value: id }) => id === workflow
  );
  const selectedEvent = WORKFLOW_EVENT_OPTIONS.find(
    ({ value: eventValue, negation: eventNegation }) => {
      const [eventType] = eventValue.split(":");
      return eventType === event && negated === eventNegation;
    }
  );
  const showTimeframeOptions = showOptions && !RELATIVE_EVENTS.has(event);
  const showRelativeOptions = showOptions && RELATIVE_EVENTS.has(event);
  const handleChange = (changes, nextNegated = negated) => {
    var _a;
    const updatedChanges = { ...changes };
    if (changes.event) {
      const shouldHaveTimes = !RELATIVE_EVENTS.has(changes.event);
      updatedChanges.times = shouldHaveTimes ? (_a = changes.times) != null ? _a : 1 : void 0;
    }
    onChange(
      { workflow: { ...conditions.workflow, ...updatedChanges } },
      nextNegated
    );
  };
  const handleWorkflowChange = ({ value: newWorkflow }) => handleChange({ workflow: newWorkflow });
  const handleEventChange = ({
    value: eventValue,
    negation: eventNegation
  }) => {
    const [eventType] = eventValue.split(":");
    handleChange({ event: eventType }, eventNegation);
  };
  return /* @__PURE__ */ jsxs22(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs22(Clause, {
        children: [
          /* @__PURE__ */ jsx22(Select22, {
            value: selectedWorkflow,
            options: workflowOptions,
            onChange: handleWorkflowChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx22(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx22(Select22, {
              value: selectedEvent,
              options: WORKFLOW_EVENT_OPTIONS,
              onChange: handleEventChange,
              portal: true,
              menuShouldBlockScroll: true
            })
          }),
          /* @__PURE__ */ jsxs22(Button10, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showTimeframeOptions && /* @__PURE__ */ jsx22(TimeframeOptions, {
        times,
        operator,
        value,
        onChange: handleChange
      }),
      showRelativeOptions && /* @__PURE__ */ jsx22(RelativeTimeOptions, {
        operator,
        value,
        onChange: handleChange
      })
    ]
  });
};

// src/components/WorkflowTaskClause.tsx
import { useContext as useContext16, useState as useState10 } from "react";
import { Select as Select23, Input as Input9, Button as Button11 } from "@studio/legacy-components";
import { jsx as jsx23, jsxs as jsxs23 } from "react/jsx-runtime";
var PAGE_KEY10 = "workflow-task-clause";
var WorkflowTaskClause = ({
  conditions,
  negated,
  onChange
}) => {
  const [showOptions, toggleShowOptions] = useStorageToggle(PAGE_KEY10);
  const {
    workflowTask: { workflow, task, operator, value }
  } = conditions;
  const { workflowOptions } = useContext16(ConditionsContext);
  const selectedWorkflow = workflowOptions.find(
    ({ value: id }) => id === workflow
  );
  const [draftTask, setDraftTask] = useState10(task);
  const handleChange = (changes, nextNegated = negated) => {
    onChange(
      { workflowTask: { ...conditions.workflowTask, ...changes } },
      nextNegated
    );
  };
  const handleWorkflowChange = ({
    value: nextWorkflow
  }) => handleChange({ workflow: nextWorkflow });
  const handleTaskChange = ({
    target: { value: nextTask }
  }) => setDraftTask(nextTask);
  const handleTaskBlur = ({
    target: { value: nextTask }
  }) => handleChange({ task: nextTask });
  return /* @__PURE__ */ jsxs23(ClauseWrapper, {
    children: [
      /* @__PURE__ */ jsxs23(Clause, {
        children: [
          /* @__PURE__ */ jsx23(Select23, {
            value: selectedWorkflow,
            options: workflowOptions,
            onChange: handleWorkflowChange,
            portal: true,
            menuShouldBlockScroll: true
          }),
          /* @__PURE__ */ jsx23(QualificationWrapper, {
            conditions,
            negated,
            children: /* @__PURE__ */ jsx23(Input9, {
              name: "task",
              "aria-label": "task",
              type: "text",
              value: draftTask,
              onChange: handleTaskChange,
              onBlur: handleTaskBlur
            })
          }),
          /* @__PURE__ */ jsxs23(Button11, {
            kind: "secondary",
            onClick: toggleShowOptions,
            children: [
              showOptions ? "Hide" : "Show",
              " options"
            ]
          })
        ]
      }),
      showOptions && /* @__PURE__ */ jsx23(RelativeTimeOptions, {
        operator,
        value,
        onChange: handleChange
      })
    ]
  });
};

// src/components/get-empty-clause.ts
var getEmptyClause = (type, subConditionType) => {
  switch (type) {
    case "app":
      return { app: { operator: "==", value: "" } };
    case "attributes":
      return { attributes: { attribute: "", operator: "==", value: "" } };
    case "checklists":
      return { checklists: { checklist: "", status: "completed\ndismissed" } };
    case "content":
      return { content: { content: "" } };
    case "domains":
      return { domains: { operator: "==", value: "" } };
    case "event":
      return {
        event: {
          event: subConditionType === "keyMoment" ? KEY_MOMENT_EVENT_PREFIX : ""
        }
      };
    case "experience":
      return {
        experience: {
          type: subConditionType === "embeds" ? "embeds" : "mobile",
          experience: ""
        }
      };
    case "experienceStep":
      return {
        experienceStep: {
          type: subConditionType,
          experienceStep: ""
        }
      };
    case "groups":
      return { groups: { property: "", operator: "*", value: "" } };
    case "languages":
      return { languages: { operator: "^", value: "en-us" } };
    case "properties":
      return { properties: { property: "", operator: "*", value: "" } };
    case "screen":
      return { screen: { operator: "*", value: "" } };
    case "segments":
      return { segments: { segment: "" } };
    case "trigger":
      return { trigger: { event: "" } };
    case "url":
      return { url: { operator: "^", value: "" } };
    case "email":
      return { email: { email: "" } };
    case "push":
      return { push: { push: "" } };
    case "version":
      return { version: { operator: "==", value: "" } };
    case "workflow":
      return { workflow: { workflow: "", event: "enrolled" } };
    case "workflowTask":
      return { workflowTask: { workflow: "", task: "" } };
    default:
      throw new Error("Unrecognized condition selected");
  }
};

// src/components/conditions-utils.ts
function getConditionType(condition) {
  var _a;
  return (_a = Object.keys(condition).find((key) => !key.startsWith("_"))) != null ? _a : "";
}

// src/components/actions.js
var GROUP_UPDATED = "GROUP_UPDATED";
var updateGroup = (conditions) => ({
  type: GROUP_UPDATED,
  payload: conditions
});
var CLAUSE_ADDED = "CLAUSE_ADDED";
var addClause = () => ({
  type: CLAUSE_ADDED,
  payload: {}
});
var CLAUSE_UPDATED = "CLAUSE_UPDATED";
var updateClause = (index, changes, negated) => ({
  type: CLAUSE_UPDATED,
  payload: { index, changes, negated }
});
var CLAUSE_DELETED = "CLAUSE_DELETED";
var deleteClause = (index) => ({
  type: CLAUSE_DELETED,
  payload: { index }
});
var GROUP_CLAUSE_ADDED = "GROUP_CLAUSE_ADDED";
var addGroup = () => ({
  type: GROUP_CLAUSE_ADDED,
  payload: {}
});
var GROUP_UNGROUPED = "GROUP_UNGROUPED";
var ungroup = () => ({
  type: GROUP_UNGROUPED,
  payload: {}
});
var GROUP_DELETED = "GROUP_DELETED";
var deleteGroup = () => ({
  type: GROUP_DELETED,
  payload: {}
});

// src/components/reducer.js
var conditionsReducer = (conditions, { type, payload }) => {
  const groupType = getConditionType(conditions);
  const clauses = conditions[groupType];
  switch (type) {
    case GROUP_UPDATED:
      return { [payload]: clauses };
    case CLAUSE_ADDED: {
      const [lastClause] = getLeafNodes(conditions).slice(-1);
      const lastClauseType = getConditionType(lastClause);
      return {
        [groupType]: [
          ...clauses,
          getEmptyClause(lastClauseType, lastClause[lastClauseType].type)
        ]
      };
    }
    case CLAUSE_UPDATED: {
      const { index, changes, negated = false } = payload;
      const nextClause = negated ? { not: [changes] } : changes;
      return {
        [groupType]: [
          ...clauses.slice(0, index),
          ...changes ? Array.isArray(changes) ? changes : [nextClause] : [],
          ...clauses.slice(index + 1)
        ]
      };
    }
    case CLAUSE_DELETED: {
      const { index } = payload;
      return {
        [groupType]: [...clauses.slice(0, index), ...clauses.slice(index + 1)]
      };
    }
    case GROUP_CLAUSE_ADDED: {
      const [lastClause] = getLeafNodes(conditions).slice(-1);
      const lastClauseType = getConditionType(lastClause);
      return {
        [groupType]: [
          ...clauses,
          {
            [groupType]: [
              getEmptyClause(lastClauseType, lastClause[lastClauseType].type)
            ]
          }
        ]
      };
    }
    case GROUP_UNGROUPED:
      return clauses;
    case GROUP_DELETED:
      return null;
    default:
      return conditions;
  }
};

// src/components/Group.tsx
import { Fragment as Fragment6, jsx as jsx24, jsxs as jsxs24 } from "react/jsx-runtime";
var CLAUSE_TYPES = {
  attributes: AttributesClause,
  banners: BannersClause,
  checklists: ChecklistsClause,
  content: ContentClause,
  email: EmailClause,
  embeds: EmbedsClause,
  event: EventClause,
  groups: GroupsClause,
  keyMoment: EventClause,
  languages: LanguagesClause,
  mobile: MobileContentClause,
  pins: PinsClause,
  properties: PropertiesClause,
  push: PushClause,
  screen: ScreenClause,
  segments: SegmentsClause,
  url: UrlClause,
  version: VersionClause,
  workflow: WorkflowClause,
  workflowTask: WorkflowTaskClause
};
var CLAUSE_TYPE_CONDITIONS = {
  mobile: "experience",
  embeds: "experience",
  pins: "experienceStep",
  banners: "experienceStep",
  keyMoment: "event"
};
function generateUniqueId() {
  return `key-${Math.random().toString(36).slice(2, 11)}`;
}
function stripMetaData(conditions) {
  return Object.entries(conditions).reduce(
    (acc, [key, value]) => {
      if (!key.startsWith("_")) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
}
function getConditionType2(condition) {
  var _a;
  return (_a = Object.keys(condition).find((key) => !key.startsWith("_"))) != null ? _a : "";
}
function getClauseType(subConditionType, subConditionValue, canTargetKeyMoments = true) {
  if (subConditionType === "event") {
    const isKeyMoment2 = canTargetKeyMoments && isKeyMomentEvent(subConditionValue == null ? void 0 : subConditionValue.event);
    return isKeyMoment2 ? "keyMoment" : "event";
  }
  if (subConditionType === "experience" || subConditionType === "experienceDismissed" || subConditionType === "experienceStep") {
    const experienceType = subConditionValue == null ? void 0 : subConditionValue.type;
    return experienceType === "flow" ? "content" : experienceType;
  }
  return subConditionType;
}
var Group = ({
  conditions,
  isProtected = false,
  isRoot = true,
  onChange,
  disableGroupControls = false,
  ...unhandledProps
}) => {
  const {
    canTargetChecklists,
    canTargetBanners,
    canTargetKeyMoments = false,
    canTargetWorkflows,
    bannerOptions,
    checklistOptions,
    conditionsOptions,
    emailOptions,
    embedOptions,
    eventOptions,
    keyMomentOptions = [],
    flowOptions,
    groupOptions,
    mobileFlowOptions,
    pinOptions,
    pushOptions,
    segmentOptions,
    workflowOptions,
    validClauseTypes,
    propertiesClauseOperators
  } = useContext17(ConditionsContext);
  const [[type, clauses]] = Object.entries(stripMetaData(conditions));
  const dispatch = (action) => void onChange(conditionsReducer(conditions, action));
  const [firstSubCondition] = clauses;
  const firstSubConditionType = getConditionType2(firstSubCondition);
  const hideGroupControls = isRoot && clauses.length === 1 && !["and", "or"].includes(firstSubConditionType);
  const [deletedIndex, setDeletedIndex] = useState11(0);
  const clauseIds = useMemo7(() => {
    const ids = clauses.map(() => generateUniqueId());
    if (clauses.length < ids.length) {
      return [...ids.slice(0, deletedIndex), ...ids.slice(deletedIndex + 1)];
    }
    return ids;
  }, [clauses, deletedIndex]);
  const clauseTypeOptions = validClauseTypes.filter((value) => {
    if (["workflow", "workflowTask"].includes(value)) {
      return canTargetWorkflows;
    }
    if (value === "keyMoment") {
      return canTargetKeyMoments;
    }
    return true;
  }).map((value) => {
    switch (value) {
      case "attributes":
        return { value, label: "Attributes" };
      case "banners": {
        return {
          value,
          label: "Banners",
          isDisabled: !canTargetBanners || bannerOptions.length === 0
        };
      }
      case "checklists":
        return {
          value,
          label: canTargetChecklists ? "Checklist" : /* @__PURE__ */ jsxs24(Fragment6, {
            children: [
              "Checklist",
              /* @__PURE__ */ jsx24(UpgradeBadge, {
                label: "Upgrade",
                icon: "magic"
              })
            ]
          }),
          isDisabled: !canTargetChecklists || checklistOptions.length === 0
        };
      case "content":
        return {
          value,
          label: "Flow",
          isDisabled: flowOptions.length === 0
        };
      case "email": {
        return {
          value,
          label: "Emails",
          isDisabled: emailOptions.length === 0
        };
      }
      case "embeds": {
        return {
          value,
          label: "Embeds",
          isDisabled: embedOptions.length === 0
        };
      }
      case "event":
        return {
          value,
          label: "Event",
          isDisabled: eventOptions.length === 0
        };
      case "keyMoment":
        return {
          value,
          label: "Key Moment",
          isDisabled: keyMomentOptions.length === 0
        };
      case "groups":
        return {
          value,
          label: "Group Property",
          isDisabled: groupOptions.length === 0
        };
      case "languages":
        return { value, label: "Language" };
      case "mobile":
        return {
          value,
          label: "Mobile Flow",
          isDisabled: mobileFlowOptions.length === 0
        };
      case "pins":
        return {
          value,
          label: "Pins",
          isDisabled: pinOptions.length === 0
        };
      case "properties":
        return { value, label: "User Property" };
      case "push":
        return {
          value,
          label: "Push Notification",
          isDisabled: pushOptions.length === 0
        };
      case "screen":
        return {
          value,
          label: "Screen"
        };
      case "segments":
        return {
          value,
          label: "Segment",
          isDisabled: segmentOptions.length === 0
        };
      case "url":
        return { value, label: "Path" };
      case "workflow":
        return {
          value,
          label: "Workflow",
          isDisabled: workflowOptions.length === 0
        };
      case "workflowTask":
        return {
          value,
          label: "Workflow task",
          isDisabled: workflowOptions.length === 0
        };
      default:
        return { value };
    }
  });
  const handleSubConditionChange = (index) => (changes, negated = false) => {
    dispatch(updateClause(index, changes, negated));
  };
  const handleSubConditionDelete = (index) => () => {
    setDeletedIndex(index);
    if (clauses.length === 1) {
      dispatch(deleteGroup());
    } else {
      dispatch(deleteClause(index));
    }
  };
  const handleSubConditionTypeChange = (index) => ({ value: newSubConditionType }) => {
    var _a;
    const newSubCondition = (_a = CLAUSE_TYPE_CONDITIONS[newSubConditionType]) != null ? _a : newSubConditionType;
    void handleSubConditionChange(index)(
      getEmptyClause(newSubCondition, newSubConditionType),
      false
    );
  };
  const typeLabel = type === "and" ? "All" : "Any";
  const hasGroupControls = !disableGroupControls;
  return /* @__PURE__ */ jsxs24(ConditionsWrapper, {
    ...unhandledProps,
    children: [
      !hideGroupControls && /* @__PURE__ */ jsxs24(BracketWrapper, {
        children: [
          /* @__PURE__ */ jsx24(Select24, {
            isSearchable: false,
            name: "type",
            portal: true,
            menuShouldBlockScroll: true,
            value: conditionsOptions.find(({ value }) => value === type),
            options: conditionsOptions,
            onChange: ({ value }) => void dispatch(updateGroup(value))
          }),
          /* @__PURE__ */ jsx24(Bracket, {
            type
          })
        ]
      }),
      /* @__PURE__ */ jsxs24(Content, {
        children: [
          !hideGroupControls && /* @__PURE__ */ jsxs24(ConditionsType, {
            children: [
              typeLabel,
              " of the following conditions match"
            ]
          }),
          /* @__PURE__ */ jsx24(Children, {
            children: clauses.map((subConditions, index) => {
              let not;
              const restSubConditions = subConditions;
              if (Object.hasOwn(subConditions, "not")) {
                ({ not } = subConditions);
              }
              const [subCondition] = not || [restSubConditions];
              const subConditionType = getConditionType2(subCondition);
              const subConditionValue = subCondition[subConditionType];
              const conditionsKey = clauseIds[index];
              if (["and", "or"].includes(subConditionType)) {
                return /* @__PURE__ */ jsx24(Group, {
                  id: conditionsKey,
                  conditions: subCondition,
                  isRoot: false,
                  isProtected: clauses.length === 1,
                  onChange: handleSubConditionChange(index)
                }, conditionsKey);
              }
              const clauseType = getClauseType(
                subConditionType,
                subConditionValue,
                canTargetKeyMoments
              );
              const Clause2 = CLAUSE_TYPES[clauseType];
              if (!Clause2)
                return null;
              return /* @__PURE__ */ jsxs24(Child, {
                children: [
                  clauseTypeOptions.length > 1 && /* @__PURE__ */ jsx24(Select24, {
                    value: clauseTypeOptions.find(
                      ({ value: optionType }) => optionType === clauseType
                    ),
                    options: clauseTypeOptions,
                    onChange: handleSubConditionTypeChange(index),
                    portal: true,
                    menuShouldBlockScroll: true
                  }),
                  /* @__PURE__ */ jsx24(Clause2, {
                    conditions: subCondition,
                    negated: !!not,
                    ...clauseType === "properties" ? { propertiesClauseOperators } : {},
                    ...clauseType === "keyMoment" ? { isKeyMoment: true } : {},
                    onChange: handleSubConditionChange(index)
                  }, conditionsKey),
                  !hideGroupControls && /* @__PURE__ */ jsx24(IconButton2, {
                    kind: "negative",
                    icon: "trash-alt",
                    "aria-label": "remove",
                    disabled: (isRoot || isProtected) && clauses.length === 1,
                    onClick: handleSubConditionDelete(index)
                  })
                ]
              }, conditionsKey);
            })
          }),
          hasGroupControls && /* @__PURE__ */ jsxs24(ButtonGroup, {
            children: [
              /* @__PURE__ */ jsxs24(SplitButton, {
                kind: "positive",
                children: [
                  /* @__PURE__ */ jsx24(Button12, {
                    onClick: () => void dispatch(addClause()),
                    children: "Add condition"
                  }),
                  /* @__PURE__ */ jsx24(Dropdown, {
                    "aria-label": "Open additional options",
                    name: "Open additional options",
                    forwardedAs: Button12,
                    attachment: /* @__PURE__ */ jsx24(MenuList, {
                      options: [
                        {
                          label: "Add condition",
                          onClick: () => void dispatch(addClause())
                        },
                        {
                          label: "Add condition group",
                          onClick: () => void dispatch(addGroup())
                        }
                      ]
                    }),
                    children: /* @__PURE__ */ jsx24(Icon3, {
                      icon: "chevron-down"
                    })
                  })
                ]
              }),
              !isRoot && /* @__PURE__ */ jsxs24(Button12, {
                kind: "secondary",
                onClick: () => void dispatch(ungroup()),
                children: [
                  /* @__PURE__ */ jsx24(Icon3, {
                    icon: "unlink"
                  }),
                  " Ungroup"
                ]
              })
            ]
          })
        ]
      })
    ]
  });
};

// src/components/shape.js
var AppShape = PropTypes.exact({
  app: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })
});
var URLShape = PropTypes.exact({
  url: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string
  })
});
var DomainsShape = PropTypes.exact({
  domains: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })
});
var PropertiesShape = PropTypes.exact({
  properties: PropTypes.exact({
    property: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
});
var GroupsShape = PropTypes.exact({
  groups: PropTypes.exact({
    property: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
});
var LanguagesShape = PropTypes.exact({
  languages: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })
});
var ContentShape = PropTypes.exact({
  content: PropTypes.exact({
    content: PropTypes.string.isRequired,
    operator: PropTypes.string,
    times: PropTypes.number,
    value: PropTypes.number
  })
});
var ExperienceShape = PropTypes.oneOf([
  PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    times: PropTypes.number,
    experience: PropTypes.exact({
      experience: PropTypes.string,
      type: PropTypes.oneOf(["pins", "mobile", "embeds", "flow"]),
      event: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      times: PropTypes.number,
      operator_attribute: PropTypes.string
    })
  }),
  PropTypes.exact({
    experience: PropTypes.exact({
      experience: PropTypes.string,
      event: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  })
]);
var ExperienceDismissedShape = PropTypes.exact({
  experienceDismissed: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string
  })
});
var ExperienceStepShape = PropTypes.exact({
  experienceStep: PropTypes.exact({
    experienceStep: PropTypes.string,
    type: PropTypes.oneOf(["pins", "mobile", "banners"])
  })
});
var EventShape = PropTypes.exact({
  event: PropTypes.exact({
    event: PropTypes.string.isRequired,
    operator: PropTypes.string,
    times: PropTypes.number,
    value: PropTypes.number
  })
});
var SegmentsShape = PropTypes.exact({
  segments: PropTypes.exact({
    segment: PropTypes.string
  })
});
var ChecklistsShape = PropTypes.exact({
  checklists: PropTypes.exact({
    checklist: PropTypes.string.isRequired,
    status: PropTypes.oneOf(["in_progress", "skipped", "completed\ndismissed"])
  })
});
var AttributesShape = PropTypes.exact({
  attributes: PropTypes.exact({
    attribute: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.string
  })
});
var TriggerShape = PropTypes.exact({
  trigger: PropTypes.exact({
    event: PropTypes.string
  })
});
var ScreenShape = PropTypes.exact({
  screen: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string
  })
});
var VersionShape = PropTypes.exact({
  version: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string
  })
});
var AndShape = {};
var OrShape = {};
var NotShape = {};
var ConditionsShape = PropTypes.oneOfType([
  AppShape,
  URLShape,
  DomainsShape,
  PropertiesShape,
  GroupsShape,
  LanguagesShape,
  ContentShape,
  EventShape,
  SegmentsShape,
  AttributesShape,
  ChecklistsShape,
  TriggerShape,
  ScreenShape,
  VersionShape,
  ExperienceShape,
  ExperienceStepShape,
  ExperienceDismissedShape,
  PropTypes.exact(AndShape),
  PropTypes.exact(OrShape),
  PropTypes.exact(NotShape)
]);
AndShape.and = PropTypes.arrayOf(ConditionsShape);
OrShape.or = PropTypes.arrayOf(ConditionsShape);
NotShape.not = PropTypes.arrayOf(ConditionsShape);
var FrequencyShape = PropTypes.oneOf(["every_time", "never", "once"]);
var FullShape = {
  conditions: ConditionsShape,
  contentType: PropTypes.oneOf([
    "journey",
    "satisfaction-survey",
    "experience"
  ]),
  createdAt: PropTypes.number,
  createdBy: PropTypes.string,
  frequency: FrequencyShape,
  goals: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  migratedFromStepId: PropTypes.string,
  name: PropTypes.string,
  nextContentId: PropTypes.string,
  published: PropTypes.bool,
  redirectNewTab: PropTypes.bool,
  redirectUrl: PropTypes.string,
  sortPriority: PropTypes.number,
  source: PropTypes.oneOf(["CONTENT-API", "CRX", "MY.APPCUES"]),
  state: PropTypes.oneOf(["ARCHIVED", "DRAFT"]),
  testFlowId: PropTypes.string,
  testVersionOf: PropTypes.string,
  throttleExempt: PropTypes.bool,
  unpublishedAt: PropTypes.number,
  unpublishedBy: PropTypes.string,
  updatedAt: PropTypes.number,
  updatedBy: PropTypes.string,
  widgetFilter: PropTypes.oneOf(["exclude", "include", "page"])
};
var allowed = [
  "conditions",
  "id",
  "contentType",
  "sortPriority",
  "published",
  "frequency",
  "goals"
];
var parse = (rule) => pick(rule, allowed);
var Shape = PropTypes.shape(parse(FullShape));
var OptionShape = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
});
var OptionsListShape = PropTypes.arrayOf(OptionShape);
var SelectOptionsShape = PropTypes.oneOfType([
  OptionsListShape,
  PropTypes.shape({
    label: PropTypes.string,
    options: OptionsListShape
  })
]);
var ConditionsPropsShape = PropTypes.shape({
  checklistOptions: SelectOptionsShape,
  eventOptions: SelectOptionsShape,
  keyMomentOptions: SelectOptionsShape,
  flowOptions: SelectOptionsShape,
  mobileFlowOptions: SelectOptionsShape,
  groupOptions: SelectOptionsShape,
  hasChecklistTargeting: PropTypes.bool,
  hasKeyMomentTargeting: PropTypes.bool,
  propertyOptions: SelectOptionsShape,
  segmentOptions: SelectOptionsShape,
  validClauseTypes: PropTypes.arrayOf(
    PropTypes.oneOf(Object.keys(CLAUSE_TYPES))
  ),
  pinOptions: SelectOptionsShape,
  bannerOptions: SelectOptionsShape,
  hasBannerTargeting: PropTypes.bool,
  embedOptions: SelectOptionsShape
});

// src/components/ReadableConditions/ChecklistCondition.jsx
import React24 from "react";
import PropTypes2 from "prop-types";
import { Link as LegacyLink } from "@studio/legacy-components";

// src/components/ReadableConditions/styled.jsx
import styled4 from "styled-components";
var ConditionText = styled4.span`
  color: var(--foreground-secondary);
`;
var SubGroup = styled4.span``;
var Highlight = styled4.span`
  color: var(--foreground-primary);
`;
var OrCondition = styled4.span``;
var AndCondition = styled4.span``;

// src/components/ReadableConditions/ChecklistCondition.jsx
import { jsx as jsx25, jsxs as jsxs25 } from "react/jsx-runtime";
var CHECKLIST_STATUS_NAMES = {
  in_progress: "be in progress",
  skipped: "have been skipped",
  "completed\ndismissed": "have been completed"
};
var ChecklistCondition = ({
  checklists,
  status,
  checklist: checklistId,
  Link = LegacyLink
}) => {
  var _a;
  const checklistName = (_a = checklists == null ? void 0 : checklists[checklistId]) == null ? void 0 : _a.internalName;
  const checklistLink = /* @__PURE__ */ jsx25(Link, {
    to: `/checklists/${checklistId}/analytics`,
    target: "_blank",
    children: checklistName || checklistId
  });
  return /* @__PURE__ */ jsxs25(ConditionText, {
    children: [
      "checklist ",
      checklistLink,
      " must",
      " ",
      CHECKLIST_STATUS_NAMES[status] || "not have been seen"
    ]
  });
};
ChecklistCondition.propTypes = {
  status: PropTypes2.string,
  checklist: PropTypes2.string,
  checklists: PropTypes2.objectOf(
    PropTypes2.shape({
      internalName: PropTypes2.string,
      id: PropTypes2.string
    })
  ),
  Link: PropTypes2.elementType
};
var ChecklistCondition_default = ChecklistCondition;

// src/components/ReadableConditions/FlowCondition.jsx
import React25 from "react";
import PropTypes3 from "prop-types";
import { Link as LegacyLink2 } from "@studio/legacy-components";

// src/lib/date.js
import moment from "moment";
var format = (date, tokens) => {
  return moment(date).format(tokens);
};

// src/components/ReadableConditions/utils.js
var generateTimesText = (times) => {
  if (!times)
    return "";
  return `at least ${times} time${times > 1 ? "s" : ""}`;
};
var generateTimeframeText = (operator, value) => {
  if (!value || Number.isNaN(value))
    return "";
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  if (operator === "< ago") {
    return `in the last ${numOfDays} day${numOfDays > 1 ? "s" : ""}`;
  }
  if (operator === "since") {
    return `since ${format(value, "ddd MMM D YYYY")}`;
  }
  return "";
};

// src/components/ReadableConditions/FlowCondition.jsx
import { jsx as jsx26, jsxs as jsxs26 } from "react/jsx-runtime";
var EVENT_VERBS = {
  "appcues:v2:experience_started": "seen",
  "appcues:v2:experience_completed": "completed",
  "appcues:v2:experience_dismissed": "dismissed"
};
var FlowCondition = ({
  operator = "",
  value,
  times,
  negated,
  content: stepId,
  experience: experienceId,
  event,
  flows = {},
  flowExperiences = {},
  Link = LegacyLink2
}) => {
  var _a;
  if (experienceId) {
    const negative = negated || operator === "!=";
    const flowName2 = ((_a = flowExperiences == null ? void 0 : flowExperiences[experienceId]) == null ? void 0 : _a.name) || experienceId;
    const verb2 = EVENT_VERBS[event] || "seen";
    const flowLink2 = /* @__PURE__ */ jsx26(Link, {
      to: `/flows-v2/${experienceId}/analytics`,
      target: "_blank",
      children: flowName2
    });
    return /* @__PURE__ */ jsxs26(ConditionText, {
      children: [
        "flow ",
        flowLink2,
        " has ",
        negative ? "not " : "",
        "been ",
        verb2,
        " ",
        generateTimesText(times),
        " ",
        generateTimeframeText(operator, value)
      ]
    });
  }
  const legacyTimes = times != null ? times : 1;
  const flowOptions = Object.values(flows).flatMap(
    ({ name: flowName2, steps = {}, id, ...rest }) => {
      var _a2;
      return ((_a2 = Object.values(steps)) != null ? _a2 : []).map((step2) => {
        var _a3;
        return {
          value: step2 == null ? void 0 : step2.id,
          label: `${flowName2} (Step ${((_a3 = step2 == null ? void 0 : step2.index) != null ? _a3 : 0) + 1})`,
          stepType: step2 == null ? void 0 : step2.stepType,
          flowId: id,
          ...rest
        };
      });
    }
  );
  const step = flowOptions.find((option) => option.value === stepId);
  const verb = step ? getCompletionEventLabel(step.stepType) : "seen";
  const flowName = step == null ? void 0 : step.label;
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const flowLink = /* @__PURE__ */ jsx26(Link, {
    to: `/flows/${step == null ? void 0 : step.flowId}/analytics`,
    target: "_blank",
    children: flowName || stepId
  });
  return /* @__PURE__ */ jsxs26(ConditionText, {
    children: [
      "flow ",
      flowLink,
      " has ",
      negated ? "not" : "",
      " been ",
      verb,
      " at least",
      " ",
      /* @__PURE__ */ jsx26(Highlight, {
        children: legacyTimes
      }),
      " time",
      legacyTimes > 1 ? "s" : "",
      " ",
      operator === "< ago" && `in the last ${numOfDays} day${numOfDays > 1 ? "s" : ""}`,
      operator === "since" && `since ${format(value, "ddd MMM D YYYY")}`
    ]
  });
};
FlowCondition.propTypes = {
  content: PropTypes3.string,
  experience: PropTypes3.string,
  event: PropTypes3.string,
  operator: PropTypes3.string,
  flows: PropTypes3.objectOf(
    PropTypes3.shape({
      name: PropTypes3.string
    })
  ),
  flowExperiences: PropTypes3.objectOf(
    PropTypes3.shape({
      name: PropTypes3.string
    })
  ),
  value: PropTypes3.number,
  times: PropTypes3.number,
  negated: PropTypes3.bool,
  Link: PropTypes3.elementType
};
var FlowCondition_default = FlowCondition;

// src/components/ReadableConditions/LanguageCondition.jsx
import React26 from "react";
import PropTypes4 from "prop-types";
import { jsx as jsx27, jsxs as jsxs27 } from "react/jsx-runtime";
var LanguageCondition = ({ value, negated }) => {
  var _a;
  const language = (_a = LANGUAGE_OPTIONS.find((item) => item.value === value)) == null ? void 0 : _a.label;
  return /* @__PURE__ */ jsxs27(ConditionText, {
    children: [
      "language must ",
      negated ? "not" : "",
      " be ",
      /* @__PURE__ */ jsx27(Highlight, {
        children: language
      })
    ]
  });
};
LanguageCondition.propTypes = {
  value: PropTypes4.string.isRequired,
  negated: PropTypes4.bool
};
var LanguageCondition_default = LanguageCondition;

// src/components/ReadableConditions/SegmentCondition.jsx
import React27 from "react";
import { Link as LegacyLink3 } from "@studio/legacy-components";
import PropTypes5 from "prop-types";
import { jsx as jsx28, jsxs as jsxs28 } from "react/jsx-runtime";
var SegmentCondition = ({
  negated,
  segment: segmentId,
  segments,
  Link = LegacyLink3
}) => {
  var _a;
  const segmentName = (_a = segments == null ? void 0 : segments[segmentId]) == null ? void 0 : _a.name;
  return /* @__PURE__ */ jsxs28(ConditionText, {
    children: [
      "user must",
      /* @__PURE__ */ jsx28(Highlight, {
        children: negated ? " not" : ""
      }),
      " belong to",
      " ",
      /* @__PURE__ */ jsx28(Highlight, {
        children: /* @__PURE__ */ jsx28(Link, {
          to: `/segments/${segmentId}/view`,
          children: segmentName || segmentId
        })
      })
    ]
  });
};
SegmentCondition.propTypes = {
  segment: PropTypes5.string,
  negated: PropTypes5.bool,
  segments: PropTypes5.objectOf(
    PropTypes5.shape({
      name: PropTypes5.string,
      id: PropTypes5.string
    })
  ),
  Link: PropTypes5.elementType
};
var SegmentCondition_default = SegmentCondition;

// src/components/ReadableConditions/DomainCondition.jsx
import React28 from "react";
import PropTypes6 from "prop-types";

// src/components/ReadableConditions/constants.js
var OPERATOR_NAMES = {
  "==": "equal",
  "*": "contain",
  "!=": "not equal",
  ">": "be greater than",
  "<": "be less than",
  ">=": "be greater than or equal to",
  "<=": "be less than or equal to",
  "!*": "not contain",
  "^": "starts with",
  "!^": "not start with",
  $: "ends with",
  "!$": "not end with",
  "?": "exist",
  "!?": "not exist",
  in: "be one of",
  "not in": "not be one of",
  since: "since",
  regex: "regex",
  "> ago": "have occurred more than",
  "< ago": "have occurred less than",
  within: "occur within the next",
  "< time": "have occurred before",
  "> time": "have occurred after"
};

// src/components/ReadableConditions/DomainCondition.jsx
import { jsxs as jsxs29 } from "react/jsx-runtime";
var DomainCondition = ({ operator, value }) => /* @__PURE__ */ jsxs29(ConditionText, {
  children: [
    "environment must ",
    OPERATOR_NAMES[operator],
    " ",
    value
  ]
});
DomainCondition.propTypes = {
  operator: PropTypes6.string.isRequired,
  value: PropTypes6.string.isRequired
};

// src/components/ReadableConditions/EventCondition.jsx
import React29 from "react";
import PropTypes7 from "prop-types";
import { jsx as jsx29, jsxs as jsxs30 } from "react/jsx-runtime";
function formatEventName2(event) {
  if (isKeyMomentEvent(event)) {
    return event.slice(KEY_MOMENT_EVENT_PREFIX.length);
  }
  return event.replace("appcues_custom:", "");
}
var EventCondition = ({
  event,
  operator,
  value,
  times = 1,
  negated,
  keyMoments = {}
}) => {
  var _a;
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const hasValue = Boolean(event) && event !== KEY_MOMENT_EVENT_PREFIX;
  const displayEvent = hasValue ? (_a = keyMoments[event]) != null ? _a : formatEventName2(event) : "(not selected)";
  return /* @__PURE__ */ jsxs30(ConditionText, {
    children: [
      isKeyMomentEvent(event) ? "key moment" : "event",
      " ",
      /* @__PURE__ */ jsx29(Highlight, {
        children: displayEvent
      }),
      " has ",
      negated ? "not" : "",
      " occurred at least ",
      /* @__PURE__ */ jsx29(Highlight, {
        children: times
      }),
      " time",
      times > 1 ? "s" : "",
      " ",
      operator === "< ago" && `in the last ${numOfDays} day${numOfDays > 1 ? "s" : ""}`,
      operator === "since" && `since ${format(value, "ddd MMM D YYYY")}`
    ]
  });
};
EventCondition.propTypes = {
  event: PropTypes7.string.isRequired,
  operator: PropTypes7.string.isRequired,
  value: PropTypes7.number.isRequired,
  times: PropTypes7.number.isRequired,
  negated: PropTypes7.bool,
  keyMoments: PropTypes7.objectOf(PropTypes7.string)
};

// src/components/ReadableConditions/GroupCondition.jsx
import React30 from "react";
import PropTypes8 from "prop-types";
import { jsx as jsx30, jsxs as jsxs31 } from "react/jsx-runtime";
var GroupCondition = ({
  operator,
  value,
  property,
  groupProperties
}) => {
  const isDefaultOperator = DEFAULT_OPERATORS.has(operator) || NUMERIC_OPERATORS.has(operator);
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const customProp = groupProperties == null ? void 0 : groupProperties[property];
  return /* @__PURE__ */ jsxs31(ConditionText, {
    children: [
      "group property",
      " ",
      /* @__PURE__ */ jsx30(Highlight, {
        children: (customProp == null ? void 0 : customProp.label) || getPropertyDisplayName(property)
      }),
      " ",
      "must ",
      OPERATOR_NAMES[operator],
      " ",
      /* @__PURE__ */ jsxs31(Highlight, {
        children: [
          isDefaultOperator && value,
          MULTI_VALUE_OPERATORS.has(operator) && `${value.split("\n").length} values`,
          RELATIVE_TIME_OPERATORS.has(operator) && `
          ${numOfDays} day${numOfDays > 1 ? "s" : ""} ago
        `,
          ABSOLUTE_TIME_OPERATORS.has(operator) && `
          ${format(value, "MMMM Do YYYY")}
        `
        ]
      })
    ]
  });
};
GroupCondition.propTypes = {
  property: PropTypes8.string.isRequired,
  operator: PropTypes8.string.isRequired,
  value: PropTypes8.oneOfType([PropTypes8.string, PropTypes8.number]),
  groupProperties: PropTypes8.objectOf(
    PropTypes8.shape({
      name: PropTypes8.string,
      label: PropTypes8.string,
      id: PropTypes8.string
    })
  )
};

// src/components/ReadableConditions/PropertyCondition.jsx
import React31 from "react";
import PropTypes9 from "prop-types";
import { jsx as jsx31, jsxs as jsxs32 } from "react/jsx-runtime";
var PropertyCondition = ({
  property,
  operator,
  value,
  properties
}) => {
  const isDefaultOperator = DEFAULT_OPERATORS.has(operator) || NUMERIC_OPERATORS.has(operator);
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const customProp = properties == null ? void 0 : properties[property];
  return /* @__PURE__ */ jsxs32(ConditionText, {
    children: [
      "user property",
      " ",
      /* @__PURE__ */ jsx31(Highlight, {
        children: (customProp == null ? void 0 : customProp.label) || getPropertyDisplayName(property)
      }),
      " ",
      "must ",
      OPERATOR_NAMES[operator],
      " ",
      /* @__PURE__ */ jsxs32(Highlight, {
        children: [
          isDefaultOperator && value,
          MULTI_VALUE_OPERATORS.has(operator) && `${value.split("\n").length} values`,
          RELATIVE_TIME_OPERATORS.has(operator) && `
          ${numOfDays} day${numOfDays > 1 ? "s" : ""} ${operator === OPERATORS.WITHIN.value ? "" : "ago"}
        `,
          ABSOLUTE_TIME_OPERATORS.has(operator) && `
          ${format(Number.parseInt(value, 10), "MMMM Do YYYY")}
        `
        ]
      })
    ]
  });
};
PropertyCondition.propTypes = {
  property: PropTypes9.string.isRequired,
  operator: PropTypes9.string.isRequired,
  value: PropTypes9.oneOfType([PropTypes9.string, PropTypes9.number]).isRequired,
  properties: PropTypes9.objectOf(
    PropTypes9.shape({
      name: PropTypes9.string,
      label: PropTypes9.string,
      id: PropTypes9.string
    })
  )
};

// src/components/ReadableConditions/UrlCondition.jsx
import React32 from "react";
import PropTypes10 from "prop-types";
import { jsxs as jsxs33 } from "react/jsx-runtime";
var UrlCondition = ({ operator, value }) => /* @__PURE__ */ jsxs33(ConditionText, {
  children: [
    "path must ",
    OPERATOR_NAMES[operator],
    " ",
    value
  ]
});
UrlCondition.propTypes = {
  value: PropTypes10.string.isRequired,
  operator: PropTypes10.string.isRequired
};

// src/components/ReadableConditions/VersionCondition.jsx
import React33 from "react";
import PropTypes11 from "prop-types";
import { jsxs as jsxs34 } from "react/jsx-runtime";
var VersionCondition = ({ operator, value }) => /* @__PURE__ */ jsxs34(ConditionText, {
  children: [
    "version must ",
    OPERATOR_NAMES[operator],
    " ",
    value
  ]
});
VersionCondition.propTypes = {
  value: PropTypes11.string.isRequired,
  operator: PropTypes11.string.isRequired
};

// src/components/ReadableConditions/PinCondition.jsx
import React34, { useMemo as useMemo8 } from "react";
import PropTypes12 from "prop-types";
import { Link as LegacyLink4 } from "@studio/legacy-components";
import { jsx as jsx32, jsxs as jsxs35 } from "react/jsx-runtime";
var PinCondition = ({
  operator,
  negated,
  pins,
  experienceStep: experienceStepId,
  Link = LegacyLink4
}) => {
  const negative = negated || operator === "!=";
  const { pin: pinFound, step: stepFound } = useMemo8(() => {
    return Object.values(pins != null ? pins : {}).map((pin) => ({
      pin,
      step: pin.steps.find(({ id }) => id === experienceStepId)
    })).find(({ step }) => step !== void 0) || null;
  }, [experienceStepId, pins]);
  const pinLink = /* @__PURE__ */ jsx32(Link, {
    to: `/pins/${pinFound == null ? void 0 : pinFound.id}/analytics`,
    target: "_blank",
    children: (stepFound == null ? void 0 : stepFound.name) || experienceStepId
  });
  return /* @__PURE__ */ jsxs35(ConditionText, {
    children: [
      "Pin ",
      pinLink,
      " has ",
      negative ? "not" : "",
      " been interacted with"
    ]
  });
};
PinCondition.propTypes = {
  experienceStep: PropTypes12.string,
  operator: PropTypes12.string.isRequired,
  pins: PropTypes12.objectOf(PropTypes12.any),
  negated: PropTypes12.bool,
  Link: PropTypes12.elementType
};
var PinCondition_default = PinCondition;

// src/components/ReadableConditions/MobileFlowCondition.jsx
import React35 from "react";
import PropTypes13 from "prop-types";
import { Link as LegacyLink5 } from "@studio/legacy-components";
import { jsx as jsx33, jsxs as jsxs36 } from "react/jsx-runtime";
var MobileFlowCondition = ({
  operator = "",
  value,
  negated,
  experience: experienceId,
  mobiles,
  times,
  Link = LegacyLink5
}) => {
  var _a;
  const negative = negated || operator === "!=";
  const flowName = ((_a = mobiles == null ? void 0 : mobiles[experienceId]) == null ? void 0 : _a.name) || experienceId;
  const flowLink = /* @__PURE__ */ jsx33(Link, {
    to: `/mobile/flows/${experienceId}/analytics`,
    target: "_blank",
    children: flowName
  });
  return /* @__PURE__ */ jsxs36(ConditionText, {
    children: [
      "mobile flow ",
      flowLink,
      " has ",
      negative ? "not " : "",
      "been seen",
      " ",
      generateTimesText(times),
      " ",
      generateTimeframeText(operator, value)
    ]
  });
};
MobileFlowCondition.propTypes = {
  experience: PropTypes13.string,
  operator: PropTypes13.string,
  mobiles: PropTypes13.objectOf(ExperienceShape),
  value: PropTypes13.oneOfType([PropTypes13.number, PropTypes13.string]),
  times: PropTypes13.number,
  negated: PropTypes13.bool,
  Link: PropTypes13.elementType
};
var MobileFlowCondition_default = MobileFlowCondition;

// src/components/ReadableConditions/BannerCondition.jsx
import React36 from "react";
import PropTypes14 from "prop-types";
import { Link as LegacyLink6 } from "@studio/legacy-components";
import { jsx as jsx34, jsxs as jsxs37 } from "react/jsx-runtime";
var EXPERIENCE_DISMISSED = "permanently_dismissed";
var getBannerName = (value, experienceId, experiences, experienceStepId) => {
  var _a, _b;
  if (value === EXPERIENCE_DISMISSED)
    return ((_a = experiences == null ? void 0 : experiences[experienceId]) == null ? void 0 : _a.name) || experienceId;
  return ((_b = Object.values(experiences || {}).find(
    (experience) => experience.steps.some((step) => step.id === experienceStepId)
  )) == null ? void 0 : _b.name) || experienceStepId;
};
var BannerCondition = ({
  operator,
  negated,
  experienceStep: experienceStepId,
  experienceId,
  banners,
  value,
  Link = LegacyLink6
}) => {
  const negative = negated || operator === "!=";
  const bannerName = getBannerName(
    value,
    experienceId,
    banners,
    experienceStepId
  );
  const verb = value === EXPERIENCE_DISMISSED ? "dismissed" : "seen";
  const bannerLink = /* @__PURE__ */ jsx34(Link, {
    to: `/banners/${experienceId}/analytics`,
    target: "_blank",
    children: bannerName || experienceStepId
  });
  return /* @__PURE__ */ jsxs37(ConditionText, {
    children: [
      "Banner ",
      bannerLink,
      " has ",
      negative ? "not" : "",
      " been ",
      verb
    ]
  });
};
BannerCondition.propTypes = {
  experienceStep: PropTypes14.string,
  experienceId: PropTypes14.string,
  value: PropTypes14.string,
  operator: PropTypes14.string.isRequired,
  banners: PropTypes14.objectOf(
    PropTypes14.shape({
      name: PropTypes14.string,
      steps: PropTypes14.arrayOf(
        PropTypes14.shape({
          id: PropTypes14.string
        })
      )
    })
  ),
  negated: PropTypes14.bool,
  Link: PropTypes14.elementType
};
var BannerCondition_default = BannerCondition;

// src/components/ReadableConditions/EmbedCondition.jsx
import React37 from "react";
import PropTypes15 from "prop-types";
import { Link as LegacyLink7 } from "@studio/legacy-components";
import { jsx as jsx35, jsxs as jsxs38 } from "react/jsx-runtime";
var EVENT_VERBS2 = {
  "appcues:v2:experience_started": "seen",
  "appcues:v2:experience_completed": "completed",
  "appcues:v2:experience_dismissed": "dismissed"
};
var EmbedCondition = ({
  operator = "",
  value,
  negated,
  experience: experienceId,
  event,
  embeds,
  times,
  Link = LegacyLink7
}) => {
  var _a;
  const negative = negated || operator === "!=";
  const embedName = ((_a = embeds == null ? void 0 : embeds[experienceId]) == null ? void 0 : _a.name) || experienceId;
  const verb = EVENT_VERBS2[event] || "seen";
  const embedLink = /* @__PURE__ */ jsx35(Link, {
    to: `/embeds/${experienceId}/analytics`,
    target: "_blank",
    children: embedName
  });
  return /* @__PURE__ */ jsxs38(ConditionText, {
    children: [
      "Embed ",
      embedLink,
      " has ",
      negative ? "not " : "",
      "been ",
      verb,
      " ",
      generateTimesText(times),
      " ",
      generateTimeframeText(operator, value)
    ]
  });
};
EmbedCondition.propTypes = {
  experience: PropTypes15.string,
  event: PropTypes15.string,
  operator: PropTypes15.string,
  embeds: PropTypes15.objectOf(ExperienceShape),
  value: PropTypes15.oneOfType([PropTypes15.number, PropTypes15.string]),
  times: PropTypes15.number,
  negated: PropTypes15.bool,
  Link: PropTypes15.elementType
};
var EmbedCondition_default = EmbedCondition;

// src/components/ReadableConditions/EmailCondition.jsx
import React38 from "react";
import PropTypes16 from "prop-types";
import { Link as LegacyLink8 } from "@studio/legacy-components";
import { jsx as jsx36, jsxs as jsxs39 } from "react/jsx-runtime";
var getEmailName = (emailId, emails) => {
  var _a;
  return ((_a = emails == null ? void 0 : emails[emailId]) == null ? void 0 : _a.name) || emailId;
};
var generateEventText = (event, negated) => {
  switch (event) {
    case "delivered":
      return negated ? "has not been delivered" : "has been delivered";
    case "opened":
      return negated ? "has not been opened" : "has been opened";
    case "clicked":
      return negated ? "has not been clicked" : "has been clicked";
    case "spam":
      return negated ? "was not marked spam" : "marked as spam";
    case "failed":
      return negated ? "has not failed" : "failed";
    case "skipped":
      return negated ? "has not skipped" : "skipped";
    default:
      return negated ? `has not ${event}` : `has ${event}`;
  }
};
var generateTimesText2 = (event, times) => {
  if (event === "opened" || event === "clicked") {
    return `at least ${times} time${times > 1 ? "s" : ""}`;
  }
  return "";
};
var EmailCondition = ({
  email,
  event,
  operator,
  value,
  times = 1,
  negated,
  emails,
  Link = LegacyLink8
}) => {
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const emailLink = /* @__PURE__ */ jsx36(Link, {
    to: `/emails/${email}`,
    target: "_blank",
    children: getEmailName(email, emails)
  });
  return /* @__PURE__ */ jsxs39(ConditionText, {
    children: [
      "Email ",
      emailLink,
      " ",
      generateEventText(event, negated),
      " ",
      generateTimesText2(event, times),
      " ",
      operator === "< ago" && `in the last ${numOfDays} day${numOfDays > 1 ? "s" : ""}`,
      operator === "since" && `since ${format(value, "ddd MMM D YYYY")}`
    ]
  });
};
EmailCondition.propTypes = {
  email: PropTypes16.string.isRequired,
  event: PropTypes16.string.isRequired,
  operator: PropTypes16.string,
  value: PropTypes16.number,
  times: PropTypes16.number,
  negated: PropTypes16.bool,
  Link: PropTypes16.elementType,
  emails: PropTypes16.objectOf(
    PropTypes16.shape({
      id: PropTypes16.string,
      name: PropTypes16.string
    })
  )
};

// src/components/ReadableConditions/PushCondition.jsx
import React39 from "react";
import PropTypes17 from "prop-types";
import { Link as LegacyLink9 } from "@studio/legacy-components";
import { jsx as jsx37, jsxs as jsxs40 } from "react/jsx-runtime";
var getPushName = (pushId, pushNotifications) => {
  var _a;
  const { name, content } = (_a = pushNotifications == null ? void 0 : pushNotifications[pushId]) != null ? _a : {};
  const { title = "" } = content || {};
  return (name !== "Untitled" ? name : title) || pushId;
};
var generateEventText2 = (event, negated) => {
  switch (event) {
    case "sent":
      return negated ? "has not been sent" : "has been sent";
    case "opened":
      return negated ? "has not been opened" : "has been opened";
    case "failed":
      return negated ? "has not failed" : "failed";
    case "skipped":
      return negated ? "has not skipped" : "skipped";
    default:
      return negated ? `has not ${event}` : `has ${event}`;
  }
};
var generateText = (event) => {
  return event === "opened" ? "at least 1 time" : "";
};
var PushCondition = ({
  push,
  event,
  operator,
  value,
  negated,
  pushNotifications,
  Link = LegacyLink9
}) => {
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  const pushNotificationLink = /* @__PURE__ */ jsx37(Link, {
    to: `/push-notifications/${push}`,
    target: "_blank",
    children: getPushName(push, pushNotifications)
  });
  return /* @__PURE__ */ jsxs40(ConditionText, {
    children: [
      "Push notification ",
      pushNotificationLink,
      " ",
      generateEventText2(event, negated),
      " ",
      generateText(event),
      " ",
      operator === "< ago" && `in the last ${numOfDays} day${numOfDays > 1 ? "s" : ""}`,
      operator === "since" && `since ${format(value, "ddd MMM D YYYY")}`
    ]
  });
};
PushCondition.propTypes = {
  push: PropTypes17.string.isRequired,
  event: PropTypes17.string.isRequired,
  operator: PropTypes17.string,
  value: PropTypes17.number,
  negated: PropTypes17.bool,
  Link: PropTypes17.elementType,
  pushNotifications: PropTypes17.objectOf(
    PropTypes17.shape({
      id: PropTypes17.string,
      name: PropTypes17.string
    })
  )
};

// src/components/ReadableConditions/AttributeCondition.jsx
import React40 from "react";
import PropTypes18 from "prop-types";
import { jsx as jsx38, jsxs as jsxs41 } from "react/jsx-runtime";
var AttributeCondition = ({ operator, value, attribute }) => {
  const isDefaultOperator = DEFAULT_OPERATORS.has(operator) || NUMERIC_OPERATORS.has(operator);
  const numOfDays = Math.ceil(value / MILLISECONDS_PER_DAY2);
  return /* @__PURE__ */ jsxs41(ConditionText, {
    children: [
      "attribute ",
      /* @__PURE__ */ jsx38(Highlight, {
        children: attribute != null ? attribute : ""
      }),
      " must",
      " ",
      OPERATOR_NAMES[operator],
      " ",
      /* @__PURE__ */ jsxs41(Highlight, {
        children: [
          isDefaultOperator && value,
          MULTI_VALUE_OPERATORS.has(operator) && `${value.split("\n").length} values`,
          RELATIVE_TIME_OPERATORS.has(operator) && `
          ${numOfDays} day${numOfDays > 1 ? "s" : ""} ${operator === OPERATORS.WITHIN.value ? "" : "ago"}
        `,
          ABSOLUTE_TIME_OPERATORS.has(operator) && `
          ${format(Number.parseInt(value, 10), "MMMM Do YYYY")}
        `
        ]
      })
    ]
  });
};
AttributeCondition.propTypes = {
  operator: PropTypes18.string.isRequired,
  value: PropTypes18.oneOfType([PropTypes18.string, PropTypes18.number]).isRequired,
  attribute: PropTypes18.string.isRequired
};

// src/components/ReadableConditions/ReadableConditions.jsx
import { jsx as jsx39, jsxs as jsxs42 } from "react/jsx-runtime";
var SUB_CONDITIONS = {
  checklists: ChecklistCondition_default,
  content: FlowCondition_default,
  domains: DomainCondition,
  event: EventCondition,
  groups: GroupCondition,
  languages: LanguageCondition_default,
  properties: PropertyCondition,
  segments: SegmentCondition_default,
  url: UrlCondition,
  version: VersionCondition,
  pins: PinCondition_default,
  mobile: MobileFlowCondition_default,
  banners: BannerCondition_default,
  embeds: EmbedCondition_default,
  flow: FlowCondition_default,
  email: EmailCondition,
  push: PushCondition,
  attributes: AttributeCondition
};
var CONDITION_TYPE = {
  or: OrCondition,
  and: AndCondition
};
var ReadableConditions = ({ conditions, ...props }) => {
  var _a, _b, _c;
  const [type, subConditions] = (_a = Object.entries(conditions).find(([key]) => !key.startsWith("_"))) != null ? _a : [];
  if (!type) {
    return null;
  }
  if (["and", "or"].includes(type)) {
    return conditions[type].flatMap((clause, c) => {
      const ConditionType = CONDITION_TYPE[type];
      return [
        /* @__PURE__ */ jsx39(SubGroup, {
          children: /* @__PURE__ */ jsx39(ReadableConditions, {
            conditions: clause,
            ...props
          })
        }, c),
        ...c < subConditions.length - 1 ? [/* @__PURE__ */ jsxs42(ConditionType, {
          children: [
            " ",
            type,
            " "
          ]
        })] : []
      ];
    });
  }
  if (type === "not") {
    const [subConditionType, negatedCondition] = (_c = Object.entries((_b = subConditions == null ? void 0 : subConditions[0]) != null ? _b : {}).find(
      ([key]) => !key.startsWith("_")
    )) != null ? _c : [];
    const conditionType2 = (negatedCondition == null ? void 0 : negatedCondition.type) || subConditionType;
    const SubCondition2 = SUB_CONDITIONS[conditionType2];
    if (!SubCondition2) {
      return null;
    }
    return /* @__PURE__ */ jsx39(SubCondition2, {
      negated: true,
      ...negatedCondition,
      ...props
    });
  }
  const conditionType = (subConditions == null ? void 0 : subConditions.type) || type;
  const SubCondition = SUB_CONDITIONS[conditionType];
  if (!SubCondition) {
    return null;
  }
  return /* @__PURE__ */ jsx39(SubCondition, {
    ...subConditions,
    ...props
  });
};
ReadableConditions.propTypes = {
  conditions: ConditionsShape,
  Link: PropTypes19.elementType
};
var ReadableConditions_default = ReadableConditions;

// src/lib/strip.ts
function strip(conditions) {
  if (conditions.and) {
    return {
      and: conditions.and.map(strip)
    };
  }
  if (conditions.or) {
    return {
      or: conditions.or.map(strip)
    };
  }
  if (conditions.not) {
    return {
      not: conditions.not.map(strip)
    };
  }
  return Object.entries(conditions).reduce((acc, [key, val]) => {
    if (!key.startsWith("_")) {
      acc[key] = val;
    }
    return acc;
  }, {});
}

// src/components/Conditions.tsx
import { useMemo as useMemo9 } from "react";
import styled5 from "styled-components";

// src/components/GlobalStyle.ts
import { createGlobalStyle } from "styled-components";
var GlobalStyle_default = createGlobalStyle`
  :root {
  /* FA */
  --glyphs: 'Font Awesome 5 Free';
  /* font-size scale */
  --xx-small: 10px;
  --x-small: 12px;
  --small: 13px;
  --regular: 14px;
  --medium-large: 16px;
  --large: 20px;
  --x-large: 24px;
  --xx-large: 32px;
  /* font-weight scale */
  --light: 300;
  --normal: 400;
  --medium: 500;
  --semi-bold: 600;
  --bold: 700;
  --extra-bold: 800;
  /* COLORS */
  /* blurple */
  --luna: #eeeeff;
  --lil-guest-bathroom-soaps: #d7d7ff;
  --blurple: #5c5cff;
  --deep-work: #4343c5;
  /* pink */
  --bubble-tape: #ffedf3;
  --blep: #ffb0d0;
  --pink-power-ranger: #ff5290;
  --motorola-razr: #d6216b;
  /* cutecumber */
  --ice-ice-baby: #e8fbfa;
  --lisa-frank: #b0f2f4;
  --cutecumber: #20e0d6;
  --sea-monsters: #1eb5c4;
  --bulbasaur: #0b818b;
  /* bludacris */
  --white-walker: #e9f7ff;
  --cotton-candy: #8ad5ff;
  --bludacris: #2cb4ff;
  --blues-cues-rgb: 13, 126, 249;
  --blues-cues: rgb(var(--blues-cues-rgb));
  --smurf-blood: #0b1a38;
  /* grayscale */
  --white: #ffffff;
  --noreaster: #f5f7fa;
  --myst: #e7ecf3;
  --dirty-water: #b0b6cf;
  --sharkbait-ooh-la-la: #627293;
  --pleather-rgb: 57, 68, 85; /* #394460 */
  --pleather: rgb(var(--pleather-rgb));
  --oh-hi-dark-rgb: 36, 42, 53; /* #242A35 */
  --oh-hi-dark: rgb(var(--oh-hi-dark-rgb));
  --dark-resting: #7e89a9;
  --light-gray: #d6dbe7;
  /* yellow */
  --cheeeese: #fff5cd;
  --rubber-ducky: #ffcd02;
  --orange-line: #ffb039;
  /* tints */
  --tint-white: rgba(255, 255, 255, 0.3);
  --tint-black: rgba(0, 0, 0, 0.1);
  /* gradients */
  --blurple-haze: #5c5cff 0%, #8960ff 100%;
  --merfolk: #0bb6d9 0%, #10d5cb 100%;
  --regulus: linear-gradient(90deg, #0b1a38 0%, #3b456a 100%);
  --daba-dee-daba-die: linear-gradient(90deg, #4980ff 0%, #2eb1ff 100%);
  --grapefruit: linear-gradient(90deg, #ff5290 0%, #ff8389 100%);
  --mithril: linear-gradient(90deg, #f4f7fa 0%, #ffffff 100%);
  --mood-lighting: linear-gradient(90deg, #5c5cff 0%, #8ad5ff 100%);
  --galaxy: linear-gradient(90deg, #5c5cff 0%, #ff92c6 98.25%);
  --tropico: linear-gradient(90deg, #ff5290 0%, #ff5290 0.01%, #8960ff 100%);
  --gradient-green: linear-gradient(90deg, #108484 0%, #00c2c2 100%);
  /* ELEVATIONS */
  --level-1: 0px 2px 6px rgba(71, 88, 114, 0.1);
  --level-2: 0px 4px 16px rgba(71, 88, 114, 0.16);
  --level-3: 0px 12px 34px rgba(71, 88, 114, 0.2);
  --level-4: 0px 12px 34px rgba(71, 88, 114, 0.35);
  --button-elevation: 0px 4px 16px rgba(11, 26, 56, 0.1);
  --basement: inset 0px 0px 2px rgba(71, 88, 114, 0.4);
  /* SEMANTIC VARS */
  /* background colors */
  --overlay: rgba(36, 42, 53, 0.4);
  --primary-x-light: var(--luna);
  --primary-light: var(--lil-guest-bathroom-soaps);
  --primary: var(--blurple);
  --primary-dark: var(--deep-work);
  --primary-grad: linear-gradient(90deg, var(--blurple-haze));
  --safety-x-light: var(--ice-ice-baby);
  --safety-light: var(--lisa-frank);
  --safety: var(--cutecumber);
  --safety-dark: var(--sea-monsters);
  --safety-grad: var(--gradient-green);
  --danger-x-light: var(--bubble-tape);
  --danger-light: var(--blep);
  --danger: var(--pink-power-ranger);
  --danger-dark: var(--motorola-razr);
  --danger-grad: var(--grapefruit);
  --secondary-x-light: var(--white-walker);
  --secondary-light: var(--cotton-candy);
  --secondary: var(--bludacris);
  --secondary-dark: var(--blues-cues);
  --disabled-x-light: var(--noreaster);
  --disabled-light: var(--myst);
  --disabled: var(--dirty-water);
  --disabled-dark: var(--sharkbait-ooh-la-la);
  --warn-light: var(--cheeeese);
  --warn: var(--rubber-ducky);
  --warn-dark: var(--orange-line);
  /* text colors */
  --text-primary: var(--oh-hi-dark);
  --text-secondary: var(--sharkbait-ooh-la-la);
  --text-tertiary: var(--dirty-water);
  --heading-primary: var(--pleather);
  --heading-secondary: var(--sharkbait-ooh-la-la);
  --icon: var(--sharkbait-ooh-la-la);
  --icon-hover: var(--pleather);
  --nav-hover: rgba(92, 92, 255, 1);
  --nav: rgba(92, 92, 255, 1);
  --nav-disabled: rgba(92, 92, 255, 0.7);
  --nav-width: 220px;
  --nav-border: rgba(0, 0, 0, 0.1);
  --nav-item-active: var(--noreaster);
  --link-hover: rgba(13, 126, 249, 1);
  --link: rgba(44, 180, 255, 1);
  --link-disabled: rgba(44, 180, 255, 0.7);
  --banner-text: var(--pleather);
  --error-hover: rgba(214, 33, 107, 1);
  --error: rgba(214, 33, 107, 1);
  --error-disabled: rgba(214, 33, 107, 0.7);
  --descriptive-list-text: var(--pleather);
  /* checkbox */
  --checkbox: var(--sharkbait-ooh-la-la);
  --checkbox-hover: var(--oh-hi-dark);
  --checkbox-checked: var(--blurple);
  --checkbox-checked-hover: var(--deep-work);
  --checkbox-disabled: var(--dirty-water);
  --checkbox-selected-focus-bg: var(--lil-guest-bathroom-soaps);
  --checkbox-unselected-focus-bg: var(--myst);
  --checkbox-group-hover: var(--noreaster);
  --checkbox-color: var(--pleather);
  --checkbox-star-color: var(--dark-resting);
  --checkbox-star-checked-color: var(--orange-line);
  /* radio */
  --radio: var(--sharkbait-ooh-la-la);
  --radio-hover: var(--oh-hi-dark);
  --radio-checked: var(--blurple);
  --radio-checked-hover: var(--deep-work);
  --radio-disabled: var(--dirty-water);
  --radio-selected-focus-bg: var(--lil-guest-bathroom-soaps);
  --radio-group-hover: var(--noreaster);
  /**
   * NOTE: This is the old color to keep consistency while we migrate inputs.
   *       This should be replaced with --sharkbait-ooh-la-la
   */
  /* input */
  --input: var(--sharkbait-ooh-la-la);
  --input-text: var(--pleather);
  --input-focus: var(--oh-hi-dark);
  --input-disabled: var(--sharkbait-ooh-la-la);
  --input-disabled-bg: var(--noreaster);
  --input-clear-icon-color: var(--dark-resting);
  --input-light-border: var(--light-gray);
  --input-light-bg: var(--white);
  /* charts */
  --tooltip-header-bg: var(--noreaster);
  --tooltip-header-color: var(--pleather);
  /* select/dropdowns */
  --dropdown-bg: var(--white);
  --dropdown-text: var(--pleather);
  --dropdown-active: var(--oh-hi-dark);
  --dropdown-inactive: var(--sharkbait-ooh-la-la);
  --dropdown-disabled: var(--noreaster);
  --dropdown-disabled-text: var(--dirty-water);
  --dropdown-placeholder: var(--sharkbait-ooh-la-la);
  --dropdown-menu-shadow: var(--level-3);
  --dropdown-option-bg: var(--noreaster);
  --dropdown-option-selected: var(--blurple);
  --dropdown-icon: var(--sharkbait-ooh-la-la);
  /* accents */
  --ui-vertical-separator: var(--myst);
  --ui-horizontal-separator: var(--tint-black);
  /* nps colors */
  --nps-promoters: var(--sea-monsters);
  --nps-promoters-light: var(--ice-ice-baby);
  --nps-passives: var(--dirty-water);
  --nps-passives-light: var(--noreaster);
  --nps-detractors: var(--motorola-razr);
  --nps-detractors-light: var(--bubble-tape);
  --nps-no-responses: var(--dirty-water);
  /* tables */
  --table-text: var(--pleather);
  --table-border: var(--tint-black);
  --table-header-hover: var(--noreaster);
  --table-header-sorted: var(--smurf-blood);
  --table-row-expanded: var(--noreaster);
  /* status indicators */
  --status-text: var(--pleather);
  --status-active: var(--cutecumber);
  --status-active-bg: var(--ice-ice-baby);
  --status-inactive: var(--sharkbait-ooh-la-la);
  --status-inactive-bg: var(--noreaster);
  /* pagination */
  --pagination: var(--noreaster);
  --pagination-focus: var(--myst);
  --pagination-selected: var(--bludacris);
  --pagination-arrow: var(--sharkbait-ooh-la-la);
  /* date picker */
  --datepicker-color: var(--pleather);
  --datepicker-hover: var(--myst);
  --datepicker-header-bg: var(--noreaster);
  --datepicker-weekday-color: var(--sharkbait-ooh-la-la);
  --datepicker-outside-color: var(--myst);
  --datepicker-selected-bg: var(--luna);
  --datepicker-selected-hover: var(--lil-guest-bathroom-soaps);
  --datepicker-selected-border: var(--deep-work);
  --datepicker-disabled-bg: var(--noreaster);
  --datepicker-disabled-color: var(--sharkbait-ooh-la-la);
  --datepicker-label: var(--sharkbait-ooh-la-la);
  --datepicker-separator: var(--dirty-water);
  --datepicker-footer-bg: var(--white-walker);
  /* tooltip */
  --tooltip-bg: var(--smurf-blood);
  --tooltip-color: var(--white);
  --tooltip-caret-width: 6px;
  /* button */
  --square-button-bg: var(--luna);
  /* Switch */
  --switch-bg: rgba(var(--pleather-rgb), 0.35);
  --switch-hover-bg: rgba(var(--oh-hi-dark-rgb), 0.35);
  --switch-dot-bg: var(--white);
  --switch-dot-hover-bg: var(--myst);
  --switch-checked-bg: var(--cotton-candy);
  --switch-dot-checked-bg: var(--bludacris);
  --switch-checked-hover-bg: rgba(var(--blues-cues-rgb), 0.35);
  --switch-dot-checked-hover-bg: var(--blues-cues);
  --switch-disabled-bg: var(--myst);
  --switch-dot-disabled-bg: var(--white);
  --switch-disabled-checked-bg: var(--white-walker);
  --switch-dot-disabled-checked-bg: var(--cotton-candy);
  /* Progress */
  --progress-light: var(--noreaster);
  --progress-dark: var(--white);
  --progress-success: linear-gradient(90deg, var(--merfolk));
  --progress-error: var(--grapefruit);
  --progress-default: linear-gradient(90deg, var(--blurple-haze));
  /* Click to copy */
  --copy-alert-bg: var(--smurf-blood);
  /* User Profile */
  --activity-started-color: var(--orange-line);
  --activity-completed-color: var(--cutecumber);
  --activity-skipped-color: var(--motorola-razr);
  --activity-triggered-color: var(--blurple);
  --eligible-icon-color: var(--sea-monsters);
  --ineligible-icon-color: var(--dark-resting);
  --select-identifier-icon-color: var(--blurple);
  --criteria-type-color: var(--sharkbait-ooh-la-la);
  --ineligible-color: var(--error-hover);
  --see-more-link-color: var(--white);
  --profile-link-icon-color: var(--blurple);
  /* Skeleton */
  --skeleton-gradient: linear-gradient(
    to right,
    #f4f7fa 20%,
    #e7ecf3 50%,
    #f4f7fa 80%
  );
  --skeleton-icon: var(--dark-resting);
  /* Banner */
  --banner-info-gradient: linear-gradient(90deg, var(--blurple-haze));
  --banner-negative-gradient: linear-gradient(90deg, #d6216b 0%, #ff5290 100%);
  --banner-content: var(--white);
  /* Tab Navigation */
  --tab-text: var(--pleather);
  --tab-hover: var(--blurple);
  --tab-active: var(--blurple);
  --tab-disabled-hover: var(--oh-hi-dark);
  /* Navigation */
  --nav-install-color: var(--pleather);
  --nav-install-bg: var(--luna);
  --nav-install-hover: var(--lil-guest-bathroom-soaps);
  --nav-install-icon-color: var(--blurple);
  --nav-install-icon-hover: var(--deep-work);
  --nav-spoof-hover: var(--lil-guest-bathroom-soaps);
}
`;

// src/components/Conditions.tsx
import { jsx as jsx40, jsxs as jsxs43 } from "react/jsx-runtime";
var CONDITION_OPTIONS = [
  { label: "And", value: "and" },
  { label: "Or", value: "or" }
];
var Conditions = ({
  bannerOptions = [],
  checklistOptions = [],
  eventOptions = [],
  keyMomentOptions = [],
  flowOptions = [],
  groupOptions = [],
  emailOptions = [],
  embedOptions = [],
  pushOptions = [],
  hasBannerTargeting = false,
  hasChecklistTargeting = false,
  hasKeyMomentTargeting = false,
  hasWorkflowTargeting = false,
  mobileFlowOptions = [],
  pinOptions = [],
  propertyOptions = [],
  segmentOptions = [],
  workflowOptions = [],
  conditionsOptions = CONDITION_OPTIONS,
  validClauseTypes = Object.keys(CLAUSE_TYPES),
  propertiesClauseOperators = ALL_OPERATORS,
  onAttributeChange = () => Promise.resolve([]),
  ...unhandledProps
}) => {
  const { conditions = { and: [] } } = unhandledProps;
  const canTargetChecklists = useMemo9(() => {
    const isTargetingChecklists = getLeafNodes(conditions).some(
      (clause) => Object.hasOwn(clause, "checklists")
    );
    return isTargetingChecklists || hasChecklistTargeting;
  }, [conditions, hasChecklistTargeting]);
  const canTargetBanners = useMemo9(() => {
    const isTargetingBanner = getLeafNodes(conditions).some(
      (clause) => Object.hasOwn(clause, "experienceStep") && clause.experienceStep.type === "banners"
    );
    return isTargetingBanner || hasBannerTargeting;
  }, [conditions, hasBannerTargeting]);
  const canTargetKeyMoments = useMemo9(() => {
    const isTargetingKeyMoment = getLeafNodes(conditions).some(
      (clause) => {
        var _a;
        return Object.hasOwn(clause, "event") && isKeyMomentEvent((_a = clause.event) == null ? void 0 : _a.event);
      }
    );
    return isTargetingKeyMoment || hasKeyMomentTargeting;
  }, [conditions, hasKeyMomentTargeting]);
  const canTargetWorkflows = useMemo9(() => {
    const isTargetingWorkflow = getLeafNodes(conditions).some(
      (clause) => Object.hasOwn(clause, "workflow") || Object.hasOwn(clause, "workflowTask")
    );
    return isTargetingWorkflow || hasWorkflowTargeting;
  }, [conditions, hasWorkflowTargeting]);
  const context = useMemo9(
    () => ({
      canTargetChecklists,
      canTargetBanners,
      canTargetKeyMoments,
      canTargetWorkflows,
      checklistOptions,
      eventOptions,
      keyMomentOptions,
      flowOptions,
      mobileFlowOptions,
      groupOptions,
      hasChecklistTargeting,
      languageOptions: LANGUAGE_OPTIONS,
      propertyOptions,
      segmentOptions,
      pinOptions,
      bannerOptions,
      emailOptions,
      embedOptions,
      pushOptions,
      workflowOptions,
      conditionsOptions,
      validClauseTypes,
      propertiesClauseOperators,
      onAttributeChange
    }),
    [
      checklistOptions,
      canTargetBanners,
      canTargetChecklists,
      canTargetKeyMoments,
      canTargetWorkflows,
      eventOptions,
      keyMomentOptions,
      flowOptions,
      mobileFlowOptions,
      groupOptions,
      hasChecklistTargeting,
      propertyOptions,
      segmentOptions,
      pinOptions,
      bannerOptions,
      emailOptions,
      embedOptions,
      pushOptions,
      workflowOptions,
      conditionsOptions,
      validClauseTypes,
      propertiesClauseOperators,
      onAttributeChange
    ]
  );
  return /* @__PURE__ */ jsxs43(ConditionsContext.Provider, {
    value: context,
    children: [
      /* @__PURE__ */ jsx40(GlobalStyle_default, {}),
      /* @__PURE__ */ jsx40(Group, {
        ...unhandledProps
      })
    ]
  });
};
var Conditions_default = styled5(Conditions)``;
export {
  ABSOLUTE_TIME_OPERATORS,
  ALL_OPERATORS,
  AUDIENCES_CLAUSES,
  AndCondition,
  AndShape,
  AppShape,
  AttributesShape,
  CLAUSE_TYPES,
  ChecklistsShape,
  ConditionText,
  Conditions_default as Conditions,
  ConditionsPropsShape,
  ConditionsShape,
  ContentShape,
  DEFAULT_OPERATORS,
  DomainsShape,
  EXISTENTIAL_OPERATORS,
  EventShape,
  ExperienceDismissedShape,
  ExperienceShape,
  ExperienceStepShape,
  FrequencyShape,
  GroupsShape,
  Highlight,
  KEY_MOMENT_EVENT_PREFIX,
  LANGUAGE_OPTIONS,
  LanguagesShape,
  MILLISECONDS_PER_DAY2 as MILLISECONDS_PER_DAY,
  MULTI_VALUE_OPERATORS,
  NUMERIC_OPERATORS,
  NotShape,
  OPERATORS,
  OptionShape,
  OrCondition,
  OrShape,
  PROPERTY_CLAUSE_OPERATORS,
  PropertiesShape,
  RELATIVE_TIME_OPERATORS,
  ReadableConditions_default as ReadableConditions,
  STEP_TYPE_IDS,
  ScreenShape,
  SegmentsShape,
  SelectOptionsShape,
  Shape,
  SubGroup,
  TriggerShape,
  URLShape,
  VersionShape,
  allowed,
  buildKeyMomentNameMap,
  buildKeyMomentOptions,
  schema_default as conditionsSchema,
  evaluate,
  excludeKeyMomentEvents,
  filterImmutableExperienceSections,
  getAudienceConditions,
  getCompletionEventLabel,
  getDomains,
  getFlowTargetingSections,
  getFlowsV2TargetingSections,
  getLeafNodes,
  getMobileTargetingSections,
  getPersistentTargetingSections,
  getSegments,
  hasMatchingSegment,
  isGroupList,
  isGroupOption,
  isKeyMomentEvent,
  isValidConditions,
  keyMomentEventName,
  matchDomain,
  parse,
  parseMobileConditions,
  strip
};
//# sourceMappingURL=index.mjs.map